import { createRouter, createWebHistory } from 'vue-router'
import { resolvePaidTrafficRedirect } from './utils/paidTrafficCityRedirect'
import { defineAsyncComponent } from 'vue'
import { getFirebaseAuthModule } from './utils/firebaseAuthLazy'
import i18n from './i18n'
import LandingPage from './components/LandingPage.vue'
import LoadingView from './components/common/LoadingView.vue'
import ErrorView from './components/common/ErrorView.vue'
import Home from './components/Home.vue'
import AgentLayout from './components/agent/AgentLayout.vue'
import {
  isRouteRestrictedForCurrentUser,
  getCurrentAgentInfo,
  AGENT_ROLES
} from './utils/agentRoleUtils'
import {
  resolveCitySlug,
  getRegionForCitySlug,
  VALID_CITIES_ES_MAIN,
  VALID_CITIES_ES_PROVIDER,
  VALID_CITIES_ES_SEARCH,
  VALID_CITIES_EN_FULL,
} from '../../shared/locationRegistry.cjs'
import { resolveFindYourColivingMarketplaceSlug } from '../../shared/gscCoverageFixes.cjs'
import {
  BOOKING_INTEGRATION_ENABLED,
  BOOKING_INTEGRATION_PROVIDERS,
} from './config/bookingIntegration'
import {
  PROVIDER_SHOWCASE_SLUGS,
  isProviderShowcaseSlug,
} from './config/providerShowcase'
import { sendRouterPageView } from '@/composables/usePageTracking'

// Componente de carga mejorado con indicador y mensaje personalizable
// const LoadingView = { ... removed ... }

// Componente para errores de carga
// const ErrorView = { ... removed ... }

// Helper function using defineAsyncComponent for lazy loading with retry
const lazyLoadComponent = (loader, retryCount = 1) => defineAsyncComponent({
  loader,
  loadingComponent: LoadingView,
  errorComponent: ErrorView,
  delay: 200, // ms to wait before showing loading component
  timeout: 10000, // ms before timing out
  onError(error, retry, fail, attempts) {
    if (attempts <= retryCount) {
      retry();
    } else {
      fail();
    }
  }
});

// Función para carga mejorada con timeout, retry, y componentes de carga/error
// const enhancedImport = (importFn, options = {}) => { ... removed ... };

// Eager-load rutas críticas
const LandingView = LandingPage;
const ContractsView = () => import(/* webpackChunkName: "contracts" */ './components/Contracts.vue');
// Implementación básica de lazy loading para garantizar compatibilidad
const LoginView = () => import(/* webpackChunkName: "auth" */ './components/Login.vue');
const SignupView = () => import(/* webpackChunkName: "auth" */ './components/SignUp.vue');
const BlogPageView = () => import(/* webpackChunkName: "blog" */ './components/BlogPage.vue');
const AdvancedSearchView = lazyLoadComponent(() => import(/* webpackChunkName: "search" */ './components/AdvancedSearch.vue'));

// Componentes de búsqueda específica de colivings
// Prefetch: ruta crítica B2C. webpackPrefetch descarga el chunk en idle, evitando el delay lazy.
const ColivingAdvancedSearchView = lazyLoadComponent(() => import(/* webpackChunkName: "coliving-search", webpackPrefetch: true */ './components/ColivingAdvancedSearch.vue'));

// Componentes de Colivings públicos
const ColivingListView = lazyLoadComponent(() => import(/* webpackChunkName: "colivings" */ './components/AdvancedSearch.vue')); // Usar búsqueda avanzada como listado de colivings
const ColivingDetailView = lazyLoadComponent(() => import(/* webpackChunkName: "colivings" */ './components/ViewColivingPublic.vue'));

// Landing Pages B2B estratégicas
const SoftwareGestionColivingView = lazyLoadComponent(() => import(/* webpackChunkName: "b2b-software" */ './components/landings/SoftwareGestionColivingLanding.vue'));
const SaasColivingView = lazyLoadComponent(() => import(/* webpackChunkName: "b2b-saas" */ './components/landings/SaasColivingLanding.vue'));
const PropertyManagementView = lazyLoadComponent(() => import(/* webpackChunkName: "b2b-pms" */ './components/landings/PropertyManagementLanding.vue'));
const QueEsUnColivingView = lazyLoadComponent(() => import(/* webpackChunkName: "b2b-que-es-coliving" */ './components/landings/QueEsUnColivingLanding.vue'));
const EncuentraTuGestorView = lazyLoadComponent(() => import(/* webpackChunkName: "encuentra-gestor" */ './components/landings/EncuentraTuGestorLanding.vue'));
const PricingLandingView = lazyLoadComponent(() => import(/* webpackChunkName: "b2b-pricing" */ './components/landings/PricingLanding.vue'));
const SavingsCalculatorView = lazyLoadComponent(() => import(/* webpackChunkName: "b2b-savings" */ './components/landings/SavingsCalculatorLanding.vue'));

// Widget embebible para partners
const WidgetColivingView = () => import(/* webpackChunkName: "widget" */ './views/WidgetColivingView.vue');

// Importar el nuevo componente VerifyGuarantor
const VerifyGuarantor = () => import(/* webpackChunkName: "guarantor" */ './components/VerifyGuarantor.vue');

const routes = [
  {
    path: '/',
    name: 'Landing',
    component: LandingView,
    meta: {
      title: 'Inicio',
      requiresAuth: false,
      keepAlive: true // Mantener vivo para rápida navegación de regreso
    }
  },
  {
    path: '/por-que-elegirnos',
    name: 'ComparisonTable',
    component: LandingPage,
    meta: {
      title: 'Por qué elegirnos',
      requiresAuth: false
    },
    beforeEnter: (to, from, next) => {
      next({ path: '/', hash: '#comparison-table' });
    }
  },
  {
    path: '/vende-o-alquila',
    name: 'SellOrRent',
    component: LandingPage,
    meta: {
      title: 'Vende o alquila tu propiedad',
      requiresAuth: false,
      activateOwnerView: true
    },
    beforeEnter: (to, from, next) => {
      // Guardar en sessionStorage que debemos activar la opción "Soy propietario"
      sessionStorage.setItem('activateOwnerView', 'true');
      next({ path: '/', hash: '#hero-section' });
    }
  },
  {
    path: '/como-funciona',
    name: 'StepByStepGuide',
    component: LandingPage,
    meta: {
      title: 'Cómo funciona',
      requiresAuth: false
    },
    beforeEnter: (to, from, next) => {
      next({ path: '/', hash: '#step-by-step-guide' });
    }
  },
  {
    path: '/preguntas-frecuentes',
    name: 'FAQs',
    component: LandingPage,
    meta: {
      title: 'Preguntas frecuentes',
      requiresAuth: false
    },
    beforeEnter: (to, from, next) => {
      next({ path: '/', hash: '#faqs-section' });
    }
  },
  {
    path: '/privacidad',
    name: 'Privacy',
    component: LandingPage,
    meta: {
      title: 'Privacidad',
      requiresAuth: false
    },
    beforeEnter: (to, from, next) => {
      next({ path: '/', hash: '#privacy-section' });
    }
  },
  {
    path: '/contracts/:id',
    name: 'ViewContractPublic',
    component: () => import(/* webpackChunkName: "contract-view" */ './components/ViewContractPublic.vue'),
    props: true,
    meta: { requiresAuth: false, allowInvited: true, allowAuthenticated: true, noIndex: true }
  },
  {
    path: '/dashboard',
    component: lazyLoadComponent(() => import(/* webpackChunkName: "dashboard" */ './views/Dashboard.vue')),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/contracts' },
      {
        path: 'profile',
        component: lazyLoadComponent(() => import(/* webpackChunkName: "profile" */ './components/Profile.vue'))
      },
    ]
  },
  {
    path: '/contracts',
    component: ContractsView,
    meta: {
      requiresAuth: true,
      keepAlive: true,
      transition: 'fade'
    }
  },
  // OCULTO: Ruta de añadir contrato deshabilitada para agentes
  // {
  //   path: '/contracts/add',
  //   component: () => import('./components/AddContract.vue'),
  //   meta: { requiresAuth: true, requiresCompleteProfile: true }
  // },
  // OCULTO: Ruta de editar contrato deshabilitada para agentes
  // {
  //   path: '/contracts/edit/:id',
  //   name: 'EditContract',
  //   component: () => import('./components/EditContract.vue'),
  //   props: true,
  //   meta: { requiresAuth: true }
  // },
  {
    path: '/signup',
    component: SignupView,
    meta: {
      transition: 'fade'
    }
  },
  {
    path: '/login',
    component: LoginView,
    meta: {
      transition: 'fade'
    }
  },
  {
    path: '/forgot-password',
    component: () => import('./components/ForgotPassword.vue')
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('./components/ResetPassword.vue'),
    meta: {
      title: 'Restablecer Contraseña',
      requiresAuth: false,
      transition: 'fade'
    }
  },
  {
    path: '/email-verification-sent',
    name: 'EmailVerificationSent',
    component: () => import('./components/EmailVerificationSent.vue'),
    meta: {
      title: 'Verificación de Email Enviada',
      requiresAuth: false,
      transition: 'fade'
    }
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: () => import('./components/VerifyEmail.vue'),
    meta: {
      title: 'Verificar Email',
      requiresAuth: false,
      transition: 'fade'
    }
  },
  {
    path: '/change-email',
    name: 'ChangeEmail',
    component: () => import('./components/ChangeEmail.vue'),
    meta: {
      title: 'Cambiar Email',
      requiresAuth: false,
      transition: 'fade'
    }
  },
  {
    path: '/verify-agent-invitation',
    name: 'VerifyAgentInvitation',
    component: () => import(/* webpackChunkName: "agent-invitation" */ './components/agent/VerifyAgentInvitation.vue'),
    meta: {
      title: 'Verificar Invitación de Agente',
      requiresAuth: false,
      transition: 'fade'
    }
  },
  {
    path: '/profile',
    component: () => import('./components/Profile.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/complete-profile',
    component: () => import('./components/UserPanel.vue'),
    meta: { requiresAuth: true }
  },
  // MOVED UP: Ruta de contacto con alta prioridad
  {
    path: '/contacto',
    name: 'contacto',
    component: lazyLoadComponent(() => import('./components/Contacto.vue')),
    meta: {
      title: 'Contacto',
      requiresAuth: false
    }
  },
  {
    path: '/sobre-nosotros',
    name: 'sobre-nosotros',
    component: lazyLoadComponent(() => import('./components/SobreNosotros.vue')),
    meta: { title: 'Sobre nosotros', requiresAuth: false }
  },
  {
    path: '/about',
    redirect: '/sobre-nosotros'
  },
  {
    path: '/aviso-legal',
    name: 'aviso-legal',
    component: lazyLoadComponent(() => import('./components/AvisoLegal.vue')),
    meta: { title: 'Aviso Legal', requiresAuth: false }
  },
  {
    path: '/legal',
    redirect: '/aviso-legal'
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: () => import('./views/PrivacyPolicy.vue'),
    meta: {
      title: 'Política de Privacidad'
    }
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('./views/TermsPolicy.vue'),
    meta: {
      title: 'Términos y Condiciones'
    }
  },
  {
    path: '/cookies',
    name: 'cookies',
    component: () => import('./views/CookiesPolicy.vue'),
    meta: {
      title: 'Política de Cookies'
    }
  },
  {
    path: '/para-operadores',
    name: 'para-operadores',
    component: () => import('./views/ParaOperadores.vue'),
    meta: {
      title: 'Información para operadores'
    }
  },
  // Ruta de prueba para diagnóstico
  {
    path: '/test-contacto',
    name: 'test-contacto',
    component: () => import('./components/Contacto.vue'),
    meta: {
      title: 'Test Contacto',
      requiresAuth: false
    }
  },
  // Rutas del sistema de administración
  {
    path: '/admin',
    component: () => import('./components/AdminPanel.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Panel de Administración'
    },
    children: [
      {
        path: '',
        name: 'AdminDashboard',
        component: () => import('./components/admin/AdminStats.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Dashboard Admin'
        }
      },
      {
        path: 'analytics',
        name: 'AdminAnalytics',
        component: () => import('./components/admin/AdminAnalytics.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Analíticas'
        }
      },
      {
        path: 'colivings',
        name: 'AdminColivings',
        component: () => import('./components/admin/AdminColivings.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Gestión de Colivings'
        }
      },
      {
        path: 'colivings/:id/edit',
        name: 'AdminEditColiving',
        component: () => import(/* webpackChunkName: "edit-coliving" */ './components/agent/EditColiving.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Editar Coliving'
        },
        props: (route) => ({ id: route.params.id, adminMode: true })
      },
      {
        path: 'contracts',
        name: 'AdminContracts',
        component: () => import('./components/admin/AdminContracts.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Gestión de propiedades'
        }
      },
      {
        path: 'applications',
        name: 'AdminApplications',
        component: () => import('./components/admin/AdminApplications.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Solicitudes'
        }
      },
      {
        path: 'payments',
        name: 'AdminPayments',
        component: () => import('./components/admin/AdminPayments.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Pagos de inquilinos'
        }
      },
      {
        path: 'incidents',
        name: 'AdminIncidents',
        component: () => import('./components/admin/AdminIncidents.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Incidencias'
        }
      },
      {
        path: 'agents',
        name: 'AdminAgents',
        component: () => import('./components/admin/AdminAgents.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Gestión de Agentes'
        }
      },
      {
        path: 'partners',
        name: 'AdminPartners',
        component: () => import('./components/admin/AdminPartners.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Gestión de Partners API'
        }
      },
      {
        path: 'applicants',
        name: 'AdminApplicants',
        component: () => import('./components/admin/AdminApplicants.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Gestión de Solicitantes'
        }
      },
      {
        path: 'owners',
        name: 'AdminOwners',
        component: () => import('./components/admin/AdminOwners.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Gestión de Propietarios'
        }
      },
      {
        path: 'document-verification',
        name: 'AdminDocumentVerification',
        component: () => import('./components/admin/AdminDocumentVerification.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Verificación de Documentos'
        }
      },
      {
        path: 'leads',
        name: 'AdminLeads',
        component: () => import('./components/admin/AdminLeads.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Leads scrapeados'
        }
      },
      {
        path: 'contacts',
        name: 'AdminContacts',
        component: () => import('./components/admin/AdminContacts.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Contactos operadores'
        }
      }
    ]
  },
  // Ruta de compatibilidad para /admin-panel -> /admin
  {
    path: '/admin-panel',
    redirect: '/admin'
  },
  // ============================================
  // PORTAL DE OPERADORES — acceso por invitación
  // ============================================
  {
    path: '/operator/login',
    name: 'OperatorLogin',
    component: () => import('./components/operator/OperatorLogin.vue'),
    meta: {
      title: 'Acceso operador · Coolliving',
      requiresAuth: false,
      hideNavbar: true,
      noIndex: true,
    }
  },
  {
    path: '/operator/set-password',
    name: 'OperatorSetPassword',
    component: () => import('./components/operator/OperatorSetPassword.vue'),
    meta: {
      title: 'Establecer contraseña · Coolliving',
      requiresAuth: false,
      hideNavbar: true,
      noIndex: true,
    }
  },
  {
    path: '/operator/leads',
    name: 'OperatorLeads',
    component: () => import('./components/operator/OperatorLeads.vue'),
    meta: {
      title: 'Portal de leads · Coolliving',
      requiresAuth: false,
      requiresOperator: true,
      hideNavbar: true,
      noIndex: true,
    }
  },
  {
    path: '/operator/how-it-works',
    name: 'OperatorHowItWorks',
    component: () => import('./components/operator/OperatorHowItWorks.vue'),
    meta: {
      title: 'Cómo funciona · Coolliving',
      requiresAuth: false,
      hideNavbar: true,
      noIndex: false,
    }
  },
  {
    path: '/operator',
    redirect: '/operator/leads'
  },
  {
    path: '/search',
    name: 'AdvancedSearch',
    component: AdvancedSearchView,
    meta: {
      title: 'Búsqueda Avanzada',
      requiresAuth: false // Búsqueda pública por ahora
    }
  },
  {
    path: '/search/colivings',
    name: 'SearchColivings',
    component: AdvancedSearchView,
    meta: {
      title: 'Búsqueda de Colivings',
      requiresAuth: false
    }
  },
  // ============================================
  // SEO: LANDING PAGES B2B ESTRATÉGICAS
  // ============================================
  // IMPORTANTE: Estas rutas deben estar ANTES de la ruta /:slug del blog
  // para evitar que el middleware del blog las capture
  
  // Landing #1: Software de Gestión de Colivings (keyword TOP)
  {
    path: '/software-gestion-coliving',
    name: 'SoftwareGestionColiving',
    component: SoftwareGestionColivingView,
    meta: {
      title: 'Software de Gestión de Colivings con IA | Coolliving',
      description: 'Software líder de gestión de colivings. Automatiza contratos, pagos, inquilinos con IA. Dashboard en tiempo real, API REST.',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },
  
  // Landing #2: SaaS Coliving (alta conversión)
  {
    path: '/saas-coliving',
    name: 'SaasColiving',
    component: SaasColivingView,
    meta: {
      title: 'SaaS de Gestión de Colivings | Cloud y Escalable | Coolliving',
      description: 'Plataforma SaaS cloud para gestionar colivings. API REST, webhooks, integraciones. Desde 79€/mes.',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },

  // Landing B2B: marketplace de leads para operadores (ES + EN)
  {
    path: '/leads-marketplace',
    name: 'LeadsMarketplace',
    component: () => import(/* webpackChunkName: "b2b-leads-marketplace" */ './components/landings/OperadoresLeadsLanding.vue'),
    meta: {
      title: 'Leads de coliving para operadores | Coolliving',
      description: 'Recibe clientes verificados que buscan coliving en Madrid, Barcelona, Londres y NYC. Compra leads exclusivos bajo demanda.',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade',
    },
  },
  { path: '/operadores', redirect: '/leads-marketplace' },
  { path: '/operators', redirect: '/leads-marketplace' },
  
  // Landing #3: Property Management Software (competitiva internacional)
  {
    path: '/property-management-software',
    name: 'PropertyManagementSoftware',
    component: PropertyManagementView,
    meta: {
      title: 'Property Management Software for Colivings | Coolliving',
      description: 'Complete property management software for coliving operators. AI tenant selection, automated payments, multi-property dashboard.',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },
  
  // Landing #4: ¿Qué es un Coliving? (keyword educacional TOP)
  {
    path: '/que-es-un-coliving',
    name: 'QueEsUnColiving',
    component: QueEsUnColivingView,
    meta: {
      title: '¿Qué es un Coliving? Guía Completa 2026 | Ventajas y Precios | Coolliving',
      description: 'Descubre qué es un coliving, cómo funciona, ventajas vs piso compartido, precios desde 400€/mes. Guía completa para jóvenes profesionales y estudiantes.',
      keywords: 'que es un coliving, que es coliving, flex living, flexliving, coliving definición, coliving vs piso compartido, ventajas coliving, precio coliving, como funciona coliving, alquiler flexible, alquiler temporal, apartamentos amueblados, habitaciones en alquiler',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },
  
  // Landing #5: Encuentra tu Gestor de Coliving (propietarios buscan gestores)
  {
    path: '/encuentra-tu-gestor',
    name: 'EncuentraTuGestor',
    component: EncuentraTuGestorView,
    meta: {
      title: 'Encuentra Gestores de Coliving en tu Zona | Coolliving',
      description: '¿Tienes una propiedad vacía? Conecta con gestores profesionales de coliving. Convierte tu inmueble en negocio rentable. 0% comisiones.',
      keywords: 'gestores coliving, gestión coliving, gestor propiedad coliving, convertir piso coliving, empresas gestión coliving, gestión inmueble coliving',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },
  
  // Rutas por ciudad para gestores
  {
    path: '/encuentra-tu-gestor/:city',
    name: 'EncuentraTuGestorCity',
    component: EncuentraTuGestorView,
    meta: {
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    },
    beforeEnter: (to, from, next) => {
      const validCities = VALID_CITIES_ES_MAIN;
      const city = to.params.city.toLowerCase();
      
      if (validCities.includes(city)) {
        next();
      } else {
        next('/encuentra-tu-gestor');
      }
    }
  },
  
  // Landing #6: Planes (conversión directa B2B)
  {
    path: '/planes',
    name: 'Planes',
    component: PricingLandingView,
    meta: {
      title: 'Planes Software Gestión Coliving | Desde 79€/mes | Coolliving',
      description: 'Planes de gestión de colivings desde 0€. Plan Agente 79€/mes, Agente Pro 129€/mes o 5% comisión. Sin permanencia, 1 mes gratis.',
      keywords: 'planes software coliving, planes coliving, tarifa coliving management, coste plataforma coliving',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },
  {
    path: '/plans',
    name: 'Plans',
    component: PricingLandingView,
    meta: {
      title: 'Coliving Management Software Plans | From €79/month | Coolliving',
      description: 'Coliving management plans from €79/month. Agent Plan €79/month, Agent Pro €129/month or 5% commission. No lock-in, 1 month free.',
      keywords: 'coliving software plans, coliving management pricing, coliving platform cost, agent plan coliving, coliving software subscription',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },

  // Landing #7: Calculadora de Ahorro (lead gen B2B)
  {
    path: '/calculadora-ahorro',
    name: 'CalculadoraAhorro',
    component: SavingsCalculatorView,
    meta: {
      title: 'Calculadora de Ahorro para Colivings | Coolliving',
      description: 'Calcula cuánto puedes ahorrar automatizando la gestión de tu coliving con Coolliving. Descubre tu ROI en 30 segundos.',
      keywords: 'calculadora ahorro coliving, ROI coliving software, ahorro gestión coliving, rentabilidad coliving automatización',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },
  {
    path: '/savings-calculator',
    name: 'SavingsCalculator',
    component: SavingsCalculatorView,
    meta: {
      title: 'Coliving Savings Calculator | Coolliving',
      description: 'Calculate how much you can save by automating your coliving management with Coolliving. Discover your ROI in 30 seconds.',
      keywords: 'coliving savings calculator, coliving ROI, coliving management automation, coliving software savings',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },

  // ============================================
  // SEO: RUTAS ALTERNATIVAS EN INGLÉS/ESPAÑOL
  // ============================================
  // Rutas alternativas para las landing pages principales en otros idiomas
  
  // Alternativa EN: Encuentra tu Gestor
  {
    path: '/find-your-manager',
    name: 'FindYourManager',
    component: EncuentraTuGestorView,
    meta: {
      title: 'Find Coliving Managers in Your Area | Coolliving',
      description: 'Do you have an empty property? Connect with professional coliving managers. Turn your property into a profitable business. 0% commissions.',
      keywords: 'coliving managers, coliving management, property manager coliving, convert apartment coliving, coliving management companies',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },
  
  // Rutas por ciudad para gestores (EN)
  {
    path: '/find-your-manager/:city',
    name: 'FindYourManagerCity',
    component: EncuentraTuGestorView,
    meta: {
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    },
    beforeEnter: (to, from, next) => {
      const validCities = VALID_CITIES_ES_MAIN;
      const city = to.params.city.toLowerCase();
      
      if (validCities.includes(city)) {
        next();
      } else {
        next('/find-your-manager');
      }
    }
  },
  
  // Alternativa EN: Software de Gestión de Colivings
  {
    path: '/coliving-management-software',
    name: 'ColivingManagementSoftware',
    component: SoftwareGestionColivingView,
    meta: {
      title: 'Coliving Management Software with AI | Coolliving',
      description: 'Leading coliving management software. Automate contracts, payments, tenants with AI. Real-time dashboard, REST API.',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },
  
  // Alternativa EN: SaaS Coliving
  {
    path: '/coliving-saas',
    name: 'ColivingSaas',
    component: SaasColivingView,
    meta: {
      title: 'Coliving SaaS Platform | Cloud & Scalable | Coolliving',
      description: 'Cloud SaaS platform to manage colivings. REST API, webhooks, integrations. From €49/month.',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },
  
  // Alternativa ES: Property Management Software
  {
    path: '/software-gestion-propiedades',
    name: 'SoftwareGestionPropiedades',
    component: PropertyManagementView,
    meta: {
      title: 'Software de Gestión de Propiedades para Colivings | Coolliving',
      description: 'Software completo de gestión de propiedades para operadores de coliving. Selección IA de inquilinos, pagos automatizados, dashboard multi-propiedad.',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },
  
  // Alternativa EN: ¿Qué es un Coliving?
  {
    path: '/what-is-coliving',
    name: 'WhatIsColiving',
    component: QueEsUnColivingView,
    meta: {
      title: 'What is Coliving? Complete Guide 2026 | Benefits & Prices | Coolliving',
      description: 'Discover what coliving is, how it works, advantages vs shared apartment, prices from €400/month. Complete guide for young professionals and students.',
      keywords: 'what is coliving, coliving definition, flex living, flexliving, coliving vs shared apartment, coliving benefits, coliving price, how coliving works, flexible rental, furnished apartments, rooms for rent',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },
  
  // ============================================
  // SEO: REDIRECTS B2B - Keywords secundarias → Landing principal
  // ============================================
  // Keywords de gestión ES → Software Gestión Coliving (ES)
  {
    path: '/gestion-coliving',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/software-gestion-colivings',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/automatizacion-coliving',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/software-coliving',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/plataforma-gestion-colivings',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/software-administracion-coliving',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/coliving-propietarios',
    redirect: '/software-gestion-coliving'
  },
  
  // Keywords de gestión EN → Coliving Management Software (EN)
  {
    path: '/coliving-management',
    redirect: '/coliving-management-software'
  },
  {
    path: '/management-software-coliving',
    redirect: '/coliving-management-software'
  },
  {
    path: '/coliving-automation',
    redirect: '/coliving-management-software'
  },
  
  // Keywords de IA → Software Gestión Coliving
  {
    path: '/coliving-ia',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/coliving-inteligencia-artificial',
    redirect: '/software-gestion-coliving'
  },
  
  // Keywords de inquilinos/pagos → Software Gestión Coliving
  {
    path: '/gestion-inquilinos',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/gestion-inquilinos-coliving',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/gestion-pagos-alquiler',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/gestion-pagos-coliving',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/verificacion-inquilinos',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/verificacion-inquilinos-automatica',
    redirect: '/software-gestion-coliving'
  },
  
  // Keywords de contratos → Software Gestión Coliving
  {
    path: '/contratos-digitales-coliving',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/contratos-automaticos-coliving',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/firmas-digitales-alquiler',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/automatizacion-contratos-alquiler',
    redirect: '/software-gestion-coliving'
  },
  
  // Keywords de SaaS ES → SaaS Coliving (ES)
  {
    path: '/saas-gestion-coliving',
    redirect: '/saas-coliving'
  },
  {
    path: '/app-coliving',
    redirect: '/saas-coliving'
  },
  {
    path: '/api-coliving',
    redirect: '/saas-coliving'
  },
  {
    path: '/api-gestion-coliving',
    redirect: '/saas-coliving'
  },
  {
    path: '/dashboard-colivings',
    redirect: '/saas-coliving'
  },
  {
    path: '/b2b-coliving',
    redirect: '/saas-coliving'
  },
  
  // Keywords de SaaS EN → Coliving SaaS (EN)
  {
    path: '/saas-management-coliving',
    redirect: '/coliving-saas'
  },
  {
    path: '/coliving-platform',
    redirect: '/coliving-saas'
  },
  {
    path: '/coliving-api',
    redirect: '/coliving-saas'
  },
  {
    path: '/coliving-dashboard',
    redirect: '/coliving-saas'
  },
  
  // Keywords de property management EN → Property Management Software (EN)
  {
    path: '/property-management-spain',
    redirect: '/property-management-software'
  },
  {
    path: '/coliving-property-management',
    redirect: '/property-management-software'
  },
  {
    path: '/pms-coliving',
    redirect: '/property-management-software'
  },
  
  // Keywords de property management ES → Software Gestión Propiedades (ES)
  {
    path: '/software-residencias-estudiantes',
    redirect: '/software-gestion-propiedades'
  },
  {
    path: '/gestion-residencias-estudiantes',
    redirect: '/software-gestion-propiedades'
  },
  {
    path: '/gestion-propiedades-coliving',
    redirect: '/software-gestion-propiedades'
  },
  
  // Keywords educacionales ES → ¿Qué es un Coliving? (ES)
  {
    path: '/que-es-coliving',
    redirect: '/que-es-un-coliving'
  },
  {
    path: '/definicion-coliving',
    redirect: '/que-es-un-coliving'
  },
  {
    path: '/coliving-definicion',
    redirect: '/que-es-un-coliving'
  },
  {
    path: '/que-significa-coliving',
    redirect: '/que-es-un-coliving'
  },
  {
    path: '/coliving-que-es',
    redirect: '/que-es-un-coliving'
  },
  {
    path: '/como-funciona-coliving',
    redirect: '/que-es-un-coliving'
  },
  {
    path: '/como-funciona-un-coliving',
    redirect: '/que-es-un-coliving'
  },
  {
    path: '/ventajas-coliving',
    redirect: '/que-es-un-coliving'
  },
  {
    path: '/precio-coliving',
    redirect: '/que-es-un-coliving'
  },
  {
    path: '/cuanto-cuesta-coliving',
    redirect: '/que-es-un-coliving'
  },
  
  // Keywords educacionales EN → What is Coliving (EN)
  {
    path: '/what-is-a-coliving',
    redirect: '/what-is-coliving'
  },
  {
    path: '/coliving-definition',
    redirect: '/what-is-coliving'
  },
  {
    path: '/define-coliving',
    redirect: '/what-is-coliving'
  },
  {
    path: '/coliving-meaning',
    redirect: '/what-is-coliving'
  },
  {
    path: '/how-does-coliving-work',
    redirect: '/what-is-coliving'
  },
  {
    path: '/coliving-benefits',
    redirect: '/what-is-coliving'
  },
  {
    path: '/coliving-price',
    redirect: '/what-is-coliving'
  },
  {
    path: '/coliving-cost',
    redirect: '/what-is-coliving'
  },
  
  // Keywords de gestores → Encuentra tu Gestor
  {
    path: '/gestores-coliving',
    redirect: '/encuentra-tu-gestor'
  },
  {
    path: '/gestor-coliving',
    redirect: '/encuentra-tu-gestor'
  },
  {
    path: '/empresas-gestion-coliving',
    redirect: '/encuentra-tu-gestor'
  },
  {
    path: '/gestion-propiedad-coliving',
    redirect: '/encuentra-tu-gestor'
  },
  {
    path: '/convertir-piso-coliving',
    redirect: '/encuentra-tu-gestor'
  },
  {
    path: '/convertir-propiedad-coliving',
    redirect: '/encuentra-tu-gestor'
  },
  {
    path: '/gestores-coliving-madrid',
    redirect: '/encuentra-tu-gestor/madrid'
  },
  {
    path: '/gestores-coliving-barcelona',
    redirect: '/encuentra-tu-gestor/barcelona'
  },
  {
    path: '/gestores-coliving-valencia',
    redirect: '/encuentra-tu-gestor/valencia'
  },
  {
    path: '/propiedad-vacia',
    redirect: '/encuentra-tu-gestor'
  },
  {
    path: '/rentabilizar-propiedad',
    redirect: '/encuentra-tu-gestor'
  },
  {
    path: '/rentabilizar-piso',
    redirect: '/encuentra-tu-gestor'
  },
  
  // Keywords de gestores en inglés → Find Your Manager
  {
    path: '/coliving-managers',
    redirect: '/find-your-manager'
  },
  {
    path: '/coliving-manager',
    redirect: '/find-your-manager'
  },
  {
    path: '/property-management-coliving',
    redirect: '/find-your-manager'
  },
  {
    path: '/coliving-management-companies',
    redirect: '/find-your-manager'
  },
  {
    path: '/convert-apartment-coliving',
    redirect: '/find-your-manager'
  },
  {
    path: '/monetize-property',
    redirect: '/find-your-manager'
  },
  {
    path: '/empty-property',
    redirect: '/find-your-manager'
  },
  
  // Keywords genéricas → Software Gestión Coliving
  {
    path: '/gestion-coliving-sin-comisiones',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/gestion-propiedades-sin-comisiones',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/inmobiliaria-sin-comisiones',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/software-proptech',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/proptech-espana',
    redirect: '/software-gestion-coliving'
  },
  {
    path: '/software-inmobiliario-coliving',
    redirect: '/software-gestion-coliving'
  },
  // ============================================
  // SEO: REDIRECTS MOVIDOS A firebase.json (301 a nivel servidor)
  // ============================================
  // NOTA: Los redirects ahora se manejan en firebase.json para mejor SEO
  // Esto evita que Google vea contenido duplicado antes del redirect
  // ============================================
  // SEO: Nómadas digitales (ruta con componente real, NO redirect)
  {
    path: '/coliving-nomadas-digitales',
    redirect: '/alojamiento-nomadas-digitales'
  },
  // SEO: Erasmus y estudiantes internacionales
  {
    path: '/coliving-erasmus',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/alojamiento-erasmus',
    redirect: '/coliving-erasmus'
  },
  {
    path: '/erasmus-españa',
    redirect: '/coliving-erasmus'
  },
  {
    path: '/coliving-vs-alquiler-tradicional-cual-es-mejor',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/companeros-de-piso/:city',
    redirect: to => `/encuentratucoliving/${to.params.city}`
  },
  {
    path: '/companeros-de-piso',
    redirect: '/encuentratucoliving'
  },
  // SEO: Corta estancia
  {
    path: '/coliving-corta-estancia',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/coliving-temporal',
    redirect: '/coliving-corta-estancia'
  },
  {
    path: '/alquiler-temporal',
    redirect: '/coliving-corta-estancia'
  },
  // SEO: Variantes de "vivienda compartida"
  {
    path: '/vivienda-compartida',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/vivienda-compartida/:city',
    redirect: to => `/encuentratucoliving/${to.params.city}`
  },
  {
    path: '/encuentratucoliving',
    name: 'ColivingAdvancedSearch',
    component: ColivingAdvancedSearchView,
    meta: {
      title: 'Alquiler de Habitaciones, Coliving y Apartamentos en España — Miles Verificados | Coolliving',
      description: 'Descubre miles de habitaciones en alquiler, colivings y apartamentos amueblados verificados en España. Desde €400/mes todo incluido, alquiler flexible desde 1 mes. Madrid, Barcelona, Valencia y más. Sin comisiones.',
      keywords: 'coliving, colivings España, flex living, flexliving, alquiler de habitaciones, alquiler habitaciones, alquiler temporal, alquiler flexible, apartamentos amueblados, pisos compartidos, habitaciones compartidas, vivienda compartida, coliving Madrid, coliving Barcelona, espacios coliving, nómadas digitales, jóvenes profesionales, estudiantes, habitaciones verificadas, sin comisiones, todo incluido, residencias',
      ogTitle: 'Habitaciones, Coliving y Apartamentos en España | Coolliving',
      ogDescription: 'Miles de habitaciones, colivings y apartamentos amueblados verificados en España. Desde €400/mes todo incluido, alquiler flexible, proceso 100% online. ¡Encuentra tu espacio ideal!',
      ogImage: 'https://coolliving.io/images/Coolliving_OG.png',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },
  // ============================================
  // RUTAS SEO: PROVEEDORES DE COLIVINGS (ANTES DE CIUDADES)
  // ============================================
  // IMPORTANTE: Estas rutas deben estar ANTES de las rutas de ciudad
  // para que Vue Router las evalúe primero
  {
    path: '/encuentratucoliving/:provider',
    name: 'ColivingsByProvider',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: true,
      title: 'Colivings por Proveedor',
      isProviderSearch: true
    },
    beforeEnter: (to, from, next) => {
      // Lista de proveedores válidos
      const validProviders = [
        'urbancampus', 'gaiastays', 'kanso', 'muppy', 'enso', 'homiii',
        'spacest', 'aticco', 'haaus', 'socien', 'suiters', 'houxury',
        'colivinn', 'trc', 'node', 'colive', 'trees',
        'resa', 'yugo', 'livensa', 'collegiate',
        // Nuevos proveedores añadidos
        'flipco', 'liveit', 'habyt', 'queens', 'socialhub', 'outsite',
        'mycoliving', 'becasa', 'oceanika', 'nova', 'alegoria', 'olivar',
        'thestart', 'flaternity', 'hive',
        // Nuevos proveedores Dic 2025 - Canarias y Madrid
        'nomad', 'farhome', 'repeople', 'maraya',
        // Proveedores US - Nueva York
        'junehomes', 'furnishedquarters', 'fllat', 'aya', 'cohabs', 'outpost',
        'roomrs', 'nodenyc', 'silverman', 'reserve', 'squarelivinghub', 'blueground', 'common', 'bungalow',
        'outsitenyc', 'ollie', 'sharedeasy',
        // Proveedores UK - Londres
        'arkliving', 'gravityco', 'folkcoliving', 'masonfifth', 'cohabslondon', 'nodelondon', 'quintainliving', 'scape', 'canvas', 'lyvly', 'fizzyliving', 'vonder', 'chapter', 'aparto', 'yourtribe', 'uncle', 'dandi', 'iq', 'unite', 'bloom', 'bamboo', 'stayclub', 'coluxliving', 'thecollective', 'studentroost', 'domicoliving',
        // Proveedores ES - Boutique Apartments
        'aspasios'
      ];
      const validCities = VALID_CITIES_ES_PROVIDER;
      const param = to.params.provider.toLowerCase();

      // Verificar PRIMERO si es un proveedor válido
      if (validProviders.includes(param)) {
        // Es un proveedor, continuar con esta ruta
        next();
      } else if (validCities.includes(param)) {
        // Es una ciudad, redirigir a la ruta de ciudad (slug canónico)
        next({ name: 'ColivingAdvancedSearchCity', params: { city: resolveCitySlug(param) } });
      } else {
        // No es ni proveedor ni ciudad válido, redirigir a búsqueda general
        next('/encuentratucoliving');
      }
    }
  },
  {
    path: '/encuentratucoliving/:provider/:city',
    name: 'ColivingsByProviderCity',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: true,
      title: 'Colivings por Proveedor y Ciudad',
      isProviderSearch: true
    },
    beforeEnter: (to, from, next) => {
      const validProviders = [
        'urbancampus', 'gaiastays', 'kanso', 'muppy', 'enso', 'homiii',
        'spacest', 'aticco', 'haaus', 'socien', 'suiters', 'houxury',
        'colivinn', 'trc', 'node', 'colive', 'trees',
        'resa', 'yugo', 'livensa', 'collegiate',
        // Nuevos proveedores añadidos
        'flipco', 'liveit', 'habyt', 'queens', 'socialhub', 'outsite',
        'mycoliving', 'becasa', 'oceanika', 'nova', 'alegoria', 'olivar',
        'thestart', 'flaternity', 'hive',
        // Nuevos proveedores Dic 2025 - Canarias y Madrid
        'nomad', 'farhome', 'repeople', 'maraya',
        // Proveedores US - Nueva York
        'junehomes', 'furnishedquarters', 'fllat', 'aya', 'cohabs', 'outpost',
        'roomrs', 'nodenyc', 'silverman', 'reserve', 'squarelivinghub', 'blueground', 'common', 'bungalow',
        'outsitenyc', 'ollie', 'sharedeasy',
        // Proveedores UK - Londres
        'arkliving', 'gravityco', 'folkcoliving', 'masonfifth', 'cohabslondon', 'nodelondon', 'quintainliving', 'scape', 'canvas', 'lyvly', 'fizzyliving', 'vonder', 'chapter', 'aparto', 'yourtribe', 'uncle', 'dandi', 'iq', 'unite', 'bloom', 'bamboo', 'stayclub', 'coluxliving', 'thecollective', 'studentroost', 'domicoliving',
        // Proveedores ES - Boutique Apartments
        'aspasios'
      ];
      const validCities = VALID_CITIES_ES_PROVIDER;
      const validPriceSlugs = [
        'hasta-500', 'hasta-700', 'hasta-900', 'hasta-1200', 'hasta-1500', 'mas-de-1500',
        'up-to-1000', 'up-to-1200', 'up-to-1500', 'up-to-1800', 'up-to-2200', 'over-2200',
        'up-to-2500', 'up-to-3500', 'up-to-5000', 'over-5000',
        'baratos', 'economicos', 'hasta-400', 'hasta-600', 'hasta-800', 'hasta-1000', 'premium', 'lujo'
      ];

      const firstParam = to.params.provider.toLowerCase();
      const secondParam = to.params.city.toLowerCase();

      // Verificar PRIMERO si es proveedor + ciudad
      if (validProviders.includes(firstParam) && validCities.includes(secondParam)) {
        // ⚠️  CAMBIO SEO: Redirect 301 de /encuentratucoliving/:provider/:ciudad → /encuentratucoliving/:ciudad
        // Razón: Google prefiere la versión ciudad sola (canonical). 
        // Las páginas provider+ciudad tienen noindex, Google las rastrea pero no indexa.
        // Mejor hacer redirect 301 para consolidar link equity y evitar crawl budget desperdiciado.
        console.log(`[Router] Redirecting ${to.path} → /encuentratucoliving/${secondParam} (SEO: consolidar a canonical)`);
        next({ path: `/encuentratucoliving/${secondParam}`, replace: true });
      } else if (validCities.includes(firstParam) && validPriceSlugs.includes(secondParam)) {
        // Es ciudad + precio, redirigir a la ruta correspondiente
        next({ name: 'ColivingAdvancedSearchCityPrice', params: { city: firstParam, priceSlug: secondParam } });
      } else if (validProviders.includes(firstParam)) {
        // Proveedor válido pero segundo parámetro inválido, redirigir solo al proveedor
        next(`/encuentratucoliving/${firstParam}`);
      } else if (validCities.includes(firstParam)) {
        // Ciudad válida pero segundo parámetro inválido, redirigir solo a la ciudad
        next(`/encuentratucoliving/${firstParam}`);
      } else {
        // Ninguno es válido, redirigir a búsqueda general
        next('/encuentratucoliving');
      }
    }
  },

  // ============================================
  // RUTAS SEO: CIUDADES (DESPUÉS DE PROVEEDORES)
  // ============================================
  // IMPORTANTE: Estas rutas deben estar DESPUÉS de las rutas de proveedor
  // para evitar conflictos de matching

  // Rutas específicas para ciudades principales
  {
    path: '/encuentratucoliving/:city',
    name: 'ColivingAdvancedSearchCity',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    },
    beforeEnter: (to, from, next) => {
      // Lista de ciudades válidas (todas las que tienen colivings)
      const validCities = VALID_CITIES_ES_PROVIDER;
      const rawCity = to.params.city.toLowerCase();
      const city = resolveCitySlug(rawCity);
      if (city !== rawCity) {
        return next({ path: `/encuentratucoliving/${city}`, replace: true });
      }

      if (validCities.includes(city)) {
        next();
      } else {
        const region = getRegionForCitySlug(city);
        if (region === 'uk' || region === 'us') {
          next({ path: `/findyourcoliving/${city}`, replace: true });
        } else {
          next('/encuentratucoliving');
        }
      }
    }
  },
  // Rutas SEO para ciudad + precio (ej: /encuentratucoliving/malaga/hasta-700)
  {
    path: '/encuentratucoliving/:city/:priceSlug',
    name: 'ColivingAdvancedSearchCityPrice',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade',
      hasPriceFilter: true
    },
    beforeEnter: (to, from, next) => {
      const validCities = VALID_CITIES_ES_PROVIDER;
      const validPriceSlugs = [
        'hasta-500', 'hasta-700', 'hasta-900', 'hasta-1200', 'hasta-1500', 'mas-de-1500',
        'up-to-1000', 'up-to-1200', 'up-to-1500', 'up-to-1800', 'up-to-2200', 'over-2200',
        'up-to-2500', 'up-to-3500', 'up-to-5000', 'over-5000',
        'baratos', 'economicos', 'hasta-400', 'hasta-600', 'hasta-800', 'hasta-1000', 'premium', 'lujo'
      ];

      const rawCity = to.params.city.toLowerCase();
      const city = resolveCitySlug(rawCity);
      const priceSlug = to.params.priceSlug.toLowerCase();

      if (city !== rawCity) {
        const suffix = validPriceSlugs.includes(priceSlug) ? `/${priceSlug}` : '';
        return next({ path: `/encuentratucoliving/${city}${suffix}`, replace: true });
      }

      if (validCities.includes(city) && validPriceSlugs.includes(priceSlug)) {
        next();
      } else if (validCities.includes(city)) {
        // Ciudad válida pero precio inválido, redirigir a ciudad sin precio
        next(`/encuentratucoliving/${city}`);
      } else {
        next('/encuentratucoliving');
      }
    }
  },

  // ============================================
  // RUTAS EN INGLÉS: FIND YOUR COLIVING
  // ============================================
  // Ruta base en inglés
  {
    path: '/findyourcoliving',
    name: 'FindYourColivingEN',
    component: ColivingAdvancedSearchView,
    meta: {
      title: 'Coliving NYC & London 2026 — Furnished Rooms from $1,200/mo | No Broker Fee | Coolliving',
      description: 'No broker fee. Compare verified coliving and furnished rooms in New York City (Brooklyn, Manhattan, Queens, Jersey City) and London. Month-to-month leases, all-inclusive from $1,200/mo and £800/mo. Book online.',
      keywords: 'coliving NYC, coliving Brooklyn, coliving Manhattan, coliving Queens, furnished rooms New York, no broker fee, month to month NYC, shared apartments Brooklyn, coliving London, rooms for rent NYC, cheap coliving New York',
      ogTitle: 'Coliving NYC & London — Furnished Rooms | Coolliving',
      ogDescription: 'Verified coliving in Brooklyn, Manhattan, Queens, Jersey City and London. No broker fee, month-to-month, all-inclusive. Book 100% online.',
      ogImage: 'https://coolliving.io/images/Coolliving_OG.png',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },
  
  // Rutas por proveedor (inglés)
  {
    path: '/findyourcoliving/:provider',
    name: 'FindYourColivingByProvider',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: true,
      title: 'Colivings by Provider',
      isProviderSearch: true
    },
    beforeEnter: (to, from, next) => {
      const validProviders = [
        'urbancampus', 'gaiastays', 'kanso', 'muppy', 'enso', 'homiii',
        'spacest', 'aticco', 'haaus', 'socien', 'suiters', 'houxury',
        'colivinn', 'trc', 'node', 'colive', 'trees',
        'resa', 'yugo', 'livensa', 'collegiate',
        'flipco', 'liveit', 'habyt', 'queens', 'socialhub', 'outsite',
        'mycoliving', 'becasa', 'oceanika', 'nova', 'alegoria', 'olivar',
        'thestart', 'flaternity', 'hive', 'nomad', 'farhome', 'repeople', 'maraya',
        // Proveedores US - Nueva York
        'junehomes', 'furnishedquarters', 'fllat', 'aya', 'cohabs', 'outpost',
        'roomrs', 'nodenyc', 'silverman', 'reserve', 'squarelivinghub', 'blueground', 'common', 'bungalow',
        'outsitenyc', 'ollie', 'sharedeasy',
        // Proveedores UK - Londres
        'arkliving', 'gravityco', 'folkcoliving', 'masonfifth', 'cohabslondon', 'nodelondon', 'quintainliving', 'scape', 'canvas', 'lyvly', 'fizzyliving', 'vonder', 'chapter', 'aparto', 'yourtribe', 'uncle', 'dandi', 'iq', 'unite', 'bloom', 'bamboo', 'stayclub', 'coluxliving', 'thecollective', 'studentroost', 'domicoliving',
        // Proveedores ES - Boutique Apartments
        'aspasios'
      ];
      const validCities = VALID_CITIES_EN_FULL;
      const param = to.params.provider.toLowerCase();

      // 'queens' en la ruta EN es el borough NYC, no el proveedor español — redirect permanente
      if (param === 'queens') {
        next({ path: '/findyourcoliving/queens-nyc', replace: true });
      } else if (validProviders.includes(param)) {
        next();
      } else if (validCities.includes(param)) {
        next({ name: 'FindYourColivingCity', params: { city: param } });
      } else {
        const mapped = resolveFindYourColivingMarketplaceSlug(param);
        if (mapped && mapped !== param) {
          next({ path: `/findyourcoliving/${mapped}`, replace: true });
        } else {
          next('/findyourcoliving');
        }
      }
    }
  },
  {
    path: '/findyourcoliving/:provider/:city',
    name: 'FindYourColivingByProviderCity',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: true,
      title: 'Colivings by Provider and City',
      isProviderSearch: true
    },
    beforeEnter: (to, from, next) => {
      const validProviders = [
        'urbancampus', 'gaiastays', 'kanso', 'muppy', 'enso', 'homiii',
        'spacest', 'aticco', 'haaus', 'socien', 'suiters', 'houxury',
        'colivinn', 'trc', 'node', 'colive', 'trees',
        'resa', 'yugo', 'livensa', 'collegiate',
        'flipco', 'liveit', 'habyt', 'queens', 'socialhub', 'outsite',
        'mycoliving', 'becasa', 'oceanika', 'nova', 'alegoria', 'olivar',
        'thestart', 'flaternity', 'nomad', 'farhome', 'repeople', 'maraya',
        // Proveedores US - Nueva York
        'junehomes', 'furnishedquarters', 'fllat', 'aya', 'cohabs', 'outpost',
        'roomrs', 'nodenyc', 'silverman', 'reserve', 'squarelivinghub', 'blueground', 'common', 'bungalow',
        'outsitenyc', 'ollie', 'sharedeasy',
        // Proveedores UK - Londres
        'arkliving', 'gravityco', 'folkcoliving', 'masonfifth', 'cohabslondon', 'nodelondon', 'quintainliving', 'scape', 'canvas', 'lyvly', 'fizzyliving', 'vonder', 'chapter', 'aparto', 'yourtribe', 'uncle', 'dandi', 'iq', 'unite', 'bloom', 'bamboo', 'stayclub', 'coluxliving', 'thecollective', 'studentroost', 'domicoliving',
        // Proveedores ES - Boutique Apartments
        'aspasios'
      ];
      const validCities = VALID_CITIES_EN_FULL;
      const validPriceSlugs = [
        'hasta-500', 'hasta-700', 'hasta-900', 'hasta-1200', 'hasta-1500', 'mas-de-1500',
        'up-to-1000', 'up-to-1200', 'up-to-1500', 'up-to-1800', 'up-to-2200', 'over-2200',
        'up-to-2500', 'up-to-3500', 'up-to-5000', 'over-5000',
        'baratos', 'economicos', 'hasta-400', 'hasta-600', 'hasta-800', 'hasta-1000', 'premium', 'lujo'
      ];

      const firstParam = to.params.provider.toLowerCase();
      const secondParam = to.params.city.toLowerCase();

      if (validProviders.includes(firstParam) && validCities.includes(secondParam)) {
        // ⚠️  CAMBIO SEO: Redirect 301 de /findyourcoliving/:provider/:ciudad → /findyourcoliving/:ciudad
        // Misma razón que la versión ES: consolidar a canonical, evitar crawl budget en páginas noindex
        console.log(`[Router] Redirecting ${to.path} → /findyourcoliving/${secondParam} (SEO: consolidar a canonical)`);
        next({ path: `/findyourcoliving/${secondParam}`, replace: true });
      } else if (validCities.includes(firstParam) && validPriceSlugs.includes(secondParam)) {
        next({ name: 'FindYourColivingCityPrice', params: { city: firstParam, priceSlug: secondParam } });
      } else if (validProviders.includes(firstParam)) {
        next(`/findyourcoliving/${firstParam}`);
      } else if (validCities.includes(firstParam)) {
        next(`/findyourcoliving/${firstParam}`);
      } else {
        next('/findyourcoliving');
      }
    }
  },
  
  // Rutas por ciudad (inglés)
  {
    path: '/findyourcoliving/:city',
    name: 'FindYourColivingCity',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    },
    beforeEnter: (to, from, next) => {
      const validCities = VALID_CITIES_EN_FULL;
      const city = to.params.city.toLowerCase();

      if (validCities.includes(city)) {
        next();
      } else {
        const mapped = resolveFindYourColivingMarketplaceSlug(city);
        if (mapped && mapped !== city) {
          next({ path: `/findyourcoliving/${mapped}`, replace: true });
        } else {
          next('/findyourcoliving');
        }
      }
    }
  },
  
  // Rutas por ciudad + precio (inglés)
  {
    path: '/findyourcoliving/:city/:priceSlug',
    name: 'FindYourColivingCityPrice',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade',
      hasPriceFilter: true
    },
    beforeEnter: (to, from, next) => {
      const validCities = VALID_CITIES_EN_FULL;
      const validPriceSlugs = [
        'hasta-500', 'hasta-700', 'hasta-900', 'hasta-1200', 'hasta-1500', 'mas-de-1500',
        'up-to-1000', 'up-to-1200', 'up-to-1500', 'up-to-1800', 'up-to-2200', 'over-2200',
        'up-to-2500', 'up-to-3500', 'up-to-5000', 'over-5000',
        'baratos', 'economicos', 'hasta-400', 'hasta-600', 'hasta-800', 'hasta-1000', 'premium', 'lujo'
      ];

      const city = to.params.city.toLowerCase();
      const priceSlug = to.params.priceSlug.toLowerCase();

      if (validCities.includes(city) && validPriceSlugs.includes(priceSlug)) {
        next();
      } else if (validCities.includes(city)) {
        next(`/findyourcoliving/${city}`);
      } else {
        next('/findyourcoliving');
      }
    }
  },

  // ============================================
  // RUTAS SEO: RESIDENCIAS UNIVERSITARIAS (PBSA)
  // /residencias + ciudades — filtro operadores PBSA en ColivingAdvancedSearch.
  // ============================================
  {
    path: '/residencias',
    name: 'ResidenciasUniversitarias',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: false,
      isStudentSearch: true,
      isResidenciasPbsa: true,
    },
  },
  {
    path: '/residencias/:city',
    name: 'ResidenciasUniversitariasCity',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: false,
      isStudentSearch: true,
      isResidenciasPbsa: true,
    },
  },
  {
    path: '/residencias-estudiantes',
    redirect: '/residencias',
  },
  {
    path: '/residencias-estudiantes/:city',
    redirect: (to) => `/residencias/${String(to.params.city || '').toLowerCase()}`,
  },
  // ============================================
  // RUTAS SEO: BÚSQUEDAS GENÉRICAS ALTA DEMANDA
  // ============================================
  // "coliving españa" - landing con contenido específico (no redirect genérico)
  // Sirve el artículo guía de España para matching semántico correcto con queries "coliving españa"
  {
    path: '/coliving-espana',
    redirect: '/coliving-en-espana-guia-completa-2026'
  },
  {
    path: '/coliving-españa',
    redirect: '/coliving-en-espana-guia-completa-2026'
  },
  {
    path: '/colivings-espana',
    redirect: '/coliving-en-espana-guia-completa-2026'
  },
  {
    path: '/colivings-españa',
    redirect: '/coliving-en-espana-guia-completa-2026'
  },
  // "vivir coliving" - intención de experiencia/lifestyle
  {
    path: '/vivir-coliving',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/vivir-en-coliving',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/vivir-colivings',
    redirect: '/encuentratucoliving'
  },
  // "mejor coliving" - búsqueda comparativa genérica
  {
    path: '/mejor-coliving',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/mejores-colivings',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/mejores-colivings-españa',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/mejores-colivings-espana',
    redirect: '/encuentratucoliving'
  },
  
  // ============================================
  // RUTAS SEO: MEJOR COLIVING / COLIVING BARATO (POR CIUDAD)
  // ============================================
  {
    path: '/mejor-coliving/:city',
    name: 'BestColivingCity',
    redirect: to => `/encuentratucoliving/${to.params.city}`
  },
  {
    path: '/coliving-barato/:city',
    name: 'CheapColivingCity',
    redirect: to => `/encuentratucoliving/${to.params.city}/baratos`
  },
  {
    path: '/mejor-residencia-estudiantes/:city',
    name: 'BestStudentResidenceCity',
    redirect: to => `/residencias/${to.params.city}`
  },
  {
    path: '/residencia-estudiantes-barata/:city',
    name: 'CheapStudentResidenceCity',
    redirect: to => `/residencias/${to.params.city}`
  },
  // ============================================
  // RUTAS SEO: LANDINGS POR TIPO DE ALOJAMIENTO
  // ============================================
  {
    path: '/habitaciones-temporales',
    name: 'HabitacionesTemporales',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: false,
      title: 'Habitaciones Temporales en Alquiler — Desde 1 Mes | Coolliving',
      description: 'Encuentra habitaciones en alquiler temporal desde 1 mes en Madrid, Barcelona, Valencia y más ciudades. Todo incluido, sin comisiones.',
      isAccommodationTypeLanding: true,
      accommodationType: 'temporary-rooms',
    },
  },
  {
    path: '/habitaciones-temporales/:city',
    name: 'HabitacionesTemporalesCity',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: false,
      title: 'Habitaciones Temporales',
      isAccommodationTypeLanding: true,
      accommodationType: 'temporary-rooms',
    },
  },
  {
    path: '/habitaciones-todo-incluido',
    name: 'HabitacionesTodoIncluido',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: false,
      title: 'Habitaciones Todo Incluido — WiFi, Limpieza, Gastos | Coolliving',
      description: 'Habitaciones con gastos incluidos: WiFi, limpieza, suministros. Compara opciones en Madrid, Barcelona y más. Sin sorpresas en la factura.',
      isAccommodationTypeLanding: true,
      accommodationType: 'all-inclusive',
    },
  },
  {
    path: '/habitaciones-todo-incluido/:city',
    name: 'HabitacionesTodoIncluidoCity',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: false,
      title: 'Habitaciones Todo Incluido',
      isAccommodationTypeLanding: true,
      accommodationType: 'all-inclusive',
    },
  },
  {
    path: '/apartamentos-flexliving',
    name: 'ApartamentosFlexliving',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: false,
      title: 'Apartamentos Flexliving — Alquiler Flexible en España | Coolliving',
      description: 'Apartamentos amueblados con alquiler flexible desde 1 mes. Madrid, Barcelona, Valencia, Málaga y más. Compara precios de +40 operadores.',
      isAccommodationTypeLanding: true,
      accommodationType: 'flexliving-apartments',
    },
  },
  {
    path: '/apartamentos-flexliving/:city',
    name: 'ApartamentosFlexlivingCity',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: false,
      title: 'Apartamentos Flexliving',
      isAccommodationTypeLanding: true,
      accommodationType: 'flexliving-apartments',
    },
  },
  {
    path: '/alojamiento-nomadas-digitales',
    name: 'AlojamientoNomadas',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: false,
      title: 'Alojamiento para Nómadas Digitales en España | Coolliving',
      description: 'Colivings y espacios de trabajo para nómadas digitales en España. WiFi de alta velocidad, espacios de coworking y comunidad incluidos.',
      isAccommodationTypeLanding: true,
      accommodationType: 'digital-nomads',
    },
  },
  {
    path: '/alojamiento-nomadas-digitales/:city',
    name: 'AlojamientoNomadasCity',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: false,
      title: 'Alojamiento Nómadas Digitales',
      isAccommodationTypeLanding: true,
      accommodationType: 'digital-nomads',
    },
  },
  {
    path: '/alojamiento-temporal-profesionales',
    name: 'AlojamientoProfesionales',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: false,
      title: 'Alojamiento Temporal para Profesionales — Corta y Media Estancia | Coolliving',
      description: 'Habitaciones y apartamentos para profesionales desplazados. Estancias desde 1 mes, todo incluido, en las principales ciudades de España.',
      isAccommodationTypeLanding: true,
      accommodationType: 'professionals',
    },
  },
  {
    path: '/alojamiento-temporal-profesionales/:city',
    name: 'AlojamientoProfesionalesCity',
    component: ColivingAdvancedSearchView,
    meta: {
      requiresAuth: false,
      keepAlive: false,
      title: 'Alojamiento Temporal Profesionales',
      isAccommodationTypeLanding: true,
      accommodationType: 'professionals',
    },
  },
  // SEO: Flex living → redirect a coliving
  {
    path: '/flexliving',
    name: 'FlexLivingRedirect',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/flex-living',
    name: 'FlexLivingDashRedirect',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/flexliving/:city',
    name: 'FlexLivingCityRedirect',
    redirect: to => `/encuentratucoliving/${to.params.city}`
  },
  {
    path: '/flex-living/:city',
    name: 'FlexLivingDashCityRedirect',
    redirect: to => `/encuentratucoliving/${to.params.city}`
  },
  // SEO: Coliving para parejas
  {
    path: '/coliving-parejas',
    name: 'ColivingParejasRedirect',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/coliving-parejas/:city',
    name: 'ColivingParejasCityRedirect',
    redirect: to => `/encuentratucoliving/${to.params.city}`
  },
  // SEO: Coliving pet-friendly / mascotas
  {
    path: '/coliving-pet-friendly',
    name: 'ColivingPetFriendlyRedirect',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/coliving-mascotas',
    name: 'ColivingMascotasRedirect',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/coliving-pet-friendly/:city',
    name: 'ColivingPetFriendlyCityRedirect',
    redirect: to => `/encuentratucoliving/${to.params.city}`
  },
  // SEO: Coliving todo incluido / sin fianza
  {
    path: '/coliving-todo-incluido',
    name: 'ColivingTodoIncluidoRedirect',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/coliving-todo-incluido/:city',
    name: 'ColivingTodoIncluidoCityRedirect',
    redirect: to => `/encuentratucoliving/${to.params.city}`
  },
  {
    path: '/coliving-sin-fianza',
    name: 'ColivingSinFianzaRedirect',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/coliving-sin-fianza/:city',
    name: 'ColivingSinFianzaCityRedirect',
    redirect: to => `/encuentratucoliving/${to.params.city}`
  },
  // SEO: Nómadas digitales + ciudad
  {
    path: '/coliving-nomadas-digitales/:city',
    name: 'ColivingNomadasCityRedirect',
    redirect: to => `/alojamiento-nomadas-digitales/${to.params.city}`
  },
  {
    path: '/coliving-trabajo-remoto/:city',
    name: 'ColivingTrabajoRemotoCityRedirect',
    redirect: to => `/encuentratucoliving/${to.params.city}`
  },
  // SEO: Coliving coworking
  {
    path: '/coliving-coworking',
    name: 'ColivingCoworkingRedirect',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/coliving-coworking/:city',
    name: 'ColivingCoworkingCityRedirect',
    redirect: to => `/encuentratucoliving/${to.params.city}`
  },
  // SEO: Coliving premium / lujo
  {
    path: '/coliving-premium',
    name: 'ColivingPremiumRedirect',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/coliving-premium/:city',
    name: 'ColivingPremiumCityRedirect',
    redirect: to => `/encuentratucoliving/${to.params.city}`
  },
  {
    path: '/coliving-lujo',
    name: 'ColivingLujoRedirect',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/coliving-lujo/:city',
    name: 'ColivingLujoCityRedirect',
    redirect: to => `/encuentratucoliving/${to.params.city}`
  },
  // SEO: Vivir en comunidad / habitación privada
  {
    path: '/vivir-en-comunidad',
    name: 'VivirEnComunidadRedirect',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/vivir-en-comunidad/:city',
    name: 'VivirEnComunidadCityRedirect',
    redirect: to => `/encuentratucoliving/${to.params.city}`
  },
  {
    path: '/coliving-internacional',
    name: 'ColivingInternacionalRedirect',
    redirect: '/encuentratucoliving'
  },
  {
    path: '/coliving-internacional/:city',
    name: 'ColivingInternacionalCityRedirect',
    redirect: to => `/encuentratucoliving/${to.params.city}`
  },
  // ============================================
  // Rutas públicas de Colivings
  // Hosting: /colivings → 301 /encuentratucoliving; /colivings/** → colivingFichaMiddleware (SEO bots)
  // No confundir con /coliving/:agencySlug (escaparate agencia → index.html, sin middleware ficha)
  // ============================================
  {
    path: '/colivings',
    name: 'ColivingList',
    component: ColivingListView,
    meta: {
      title: 'Colivings Disponibles',
      requiresAuth: false,
      keepAlive: true,
      transition: 'fade'
    }
  },
  // Ruta para colivings externos con integración de reserva (ej: Homiii)
  // :source = identificador del operador (homiii, ...), :externalId = doc ID en scrapedColivings
  // DEBE estar definida ANTES de /colivings/:id para que Vue Router la priorice
  {
    path: '/colivings/:source/:externalId',
    name: 'ColivingExternalDetail',
    component: ColivingDetailView,
    props: true,
    beforeEnter: (to, from, next) => {
      // Integración Homiii desactivada → comportamiento pre-integración (búsqueda + modal lead)
      if (!BOOKING_INTEGRATION_ENABLED) {
        const source = to.params.source?.toLowerCase();
        next({
          path: source ? `/encuentratucoliving/${source}` : '/encuentratucoliving',
          replace: true,
        });
        return;
      }
      next();
    },
    meta: {
      title: 'Coliving',
      requiresAuth: false,
      allowInvited: true,
      allowAuthenticated: true,
      transition: 'fade'
    }
  },
  {
    path: '/colivings/:id',
    name: 'ColivingDetail',
    component: ColivingDetailView,
    props: true,
    meta: {
      title: 'Detalle del Coliving',
      requiresAuth: false,
      allowInvited: true,
      allowAuthenticated: true,
      transition: 'fade'
    }
  },
  // RUTA DEL WIDGET: Para embeber en sitios de partners
  {
    path: '/widget/coliving/:colivingId',
    name: 'WidgetColiving',
    component: WidgetColivingView,
    props: true,
    meta: {
      title: 'Widget Coliving',
      requiresAuth: false,
      isWidget: true, // Indicador de que es un widget
      layout: 'widget', // Layout especial sin navbar/footer
      transition: 'none' // Sin transiciones para mejor performance en iframe
    }
  },
  // RUTA DE PRUEBA: Página para probar el widget en producción
  {
    path: '/integrationtest',
    name: 'IntegrationTest',
    component: () => import(/* webpackChunkName: "integration-test" */ './views/IntegrationTestView.vue'),
    meta: {
      title: 'Test de Integración - Widget',
      requiresAuth: false,
      transition: 'fade'
    }
  },
  // PRESENTACIÓN FERIA: Servida como HTML estático
  {
    path: '/vitur2026',
    name: 'Vitur2026',
    beforeEnter() {
      window.location.href = '/vitur2026.html';
    },
    component: { render: () => null }
  },
  // RUTA DEMO: Animated Showcase de 20 segundos
  // {
  //   path: '/showcase-demo',
  //   name: 'ShowcaseDemo',
  //   component: () => import(/* webpackChunkName: "showcase-demo" */ './views/ShowcaseDemo.vue'),
  //   meta: {
  //     title: 'Animated Showcase Demo',
  //     requiresAuth: false,
  //     transition: 'fade'
  //   }
  // },
  // NUEVAS RUTAS: Escaparate de agencias propias (NO proveedores scrapeados)
  // Si es un proveedor conocido, redirect a /encuentratucoliving/:provider
  {
    path: '/coliving/:agencySlug',
    name: 'AgencyShowcase',
    beforeEnter: (to, from, next) => {
      const slug = to.params.agencySlug.toLowerCase();

      const legacyScraped = slug.match(/^coliving-([a-z0-9]+)_(\d+)$/i);
      if (legacyScraped) {
        const provider = legacyScraped[1].toLowerCase();
        const externalId = `${provider}_${legacyScraped[2]}`;
        next({ path: `/colivings/${provider}/${externalId}`, replace: true });
        return;
      }
      
      // Proveedores con integración de reserva directa (solo si flag activo)
      const bookingIntegrationProviders = BOOKING_INTEGRATION_ENABLED
        ? BOOKING_INTEGRATION_PROVIDERS
        : [];
      if (bookingIntegrationProviders.includes(slug)) {
        next(); // Carga AgencyShowcase con routeType: 'provider'
        return;
      }

      if (isProviderShowcaseSlug(slug)) {
        next(); // Escaparate completo (p. ej. Spacest + selector de ciudades)
        return;
      }
      
      // Proveedores scrapeados → redirect a búsqueda filtrada (+ homiii cuando integración off)
      const redirectProviders = [
        'urbancampus', 'gaiastays', 'kanso', 'muppy', 'enso', 'homiii', 'haaus', 'houxury',
        'aticco', 'socien', 'colivinn', 'suiters', 'trc', 'trees', 'habyt',
        'colive', 'liveit', 'smartrental', 'socialhub', 'becasa', 'alegoria', 'yugo',
        'thestart', 'flaternity', 'hive', 'repeople', 'outsite', 'olivar', 'livensa', 'nomad', 'maraya',
        'node', 'flipco', 'queens', 'mycoliving', 'nova', 'resa', 'collegiate', 'farhome', 'aspasios'
      ];
      if (redirectProviders.includes(slug)) {
        next({ path: `/encuentratucoliving/${slug}`, replace: true });
        return;
      }

      // Ciudades ES → redirect a /encuentratucoliving/:city (landing Google Ads: /coliving/madrid)
      if (VALID_CITIES_ES_MAIN.includes(slug)) {
        next({ path: `/encuentratucoliving/${slug}`, replace: true });
        return;
      }
      
      // Agencias nativas → cargar AgencyShowcase con routeType: 'slug'
      next();
    },
    component: lazyLoadComponent(() => import(/* webpackChunkName: "agency-showcase" */ './views/AgencyShowcase.vue')),
    props: route => {
      const slug = route.params.agencySlug.toLowerCase();
      const bookingIntegrationProviders = BOOKING_INTEGRATION_ENABLED
        ? BOOKING_INTEGRATION_PROVIDERS
        : [];
      if (bookingIntegrationProviders.includes(slug)) {
        return { providerSlug: slug, routeType: 'provider' };
      }
      if (PROVIDER_SHOWCASE_SLUGS.includes(slug)) {
        return { providerSlug: slug, routeType: 'provider' };
      }
      return { agencySlug: slug, routeType: 'slug' };
    },
    meta: {
      title: 'Colivings por Agencia',
      requiresAuth: false,
      keepAlive: false,
      transition: 'fade'
    }
  },
  {
    path: '/agent/:agentId',
    name: 'AgentShowcase',
    component: lazyLoadComponent(() => import(/* webpackChunkName: "agency-showcase" */ './views/AgencyShowcase.vue')),
    props: route => ({
      agentId: route.params.agentId,
      routeType: 'id'
    }),
    meta: {
      title: 'Colivings por Agente',
      requiresAuth: false,
      keepAlive: false,
      transition: 'fade'
    }
  },
  // Nueva ruta para el panel de usuario (candidatos)
  {
    path: '/user-dashboard',
    name: 'UserPanel',
    component: () => import(/* webpackChunkName: "user-panel" */ './components/UserPanel.vue'),
    meta: {
      title: 'Mi Panel de Candidato',
      requiresAuth: true,
      requiresApplicantRole: true, // Nueva restricción
      transition: 'fade'
    }
  },
  // Nueva ruta para mis solicitudes de coliving
  // {
  //   path: '/my-applications',
  //   name: 'MyApplications',
  //   component: () => import(/* webpackChunkName: "my-applications" */ './components/MyColivingApplications.vue'),
  //   meta: {
  //     title: 'Mis Solicitudes de Coliving',
  //     requiresAuth: true,
  //     requiresApplicantRole: true,
  //     transition: 'fade'
  //   }
  // },
  // Rutas del Blog
  {
    path: '/blog',
    name: 'BlogPage',
    component: BlogPageView,
    meta: {
      requiresAuth: false,
      title: 'Blog',
      keepAlive: true,
      transition: 'fade'
    }
  },
  {
    path: '/blog/:slug',
    redirect: to => `/${to.params.slug}`,
  },
  // Rutas de Agente Inmobiliario
  {
    path: '/agent-dashboard',
    component: AgentLayout,
    meta: {
      requiresAuth: true,
      requiresAgentRole: true,
      title: 'Panel de Agente'
    },
    children: [
      { path: '', name: 'AgentHome', component: () => import('./components/agent/AgentHome.vue') },
      {
        path: 'clients',
        name: 'AgentClients',
        component: () => import('./components/agent/ClientManagement.vue'),
        meta: {
          requiresAuth: true,
          requiresAgentRole: true,
          title: 'Gestión de Clientes',
          restrictedForBasicAgents: true
        }
      },
      { path: 'clients/new', name: 'AgentNewClient', component: () => import('./components/agent/ClientForm.vue') },
      {
        path: 'clients/edit/:id',
        name: 'AgentClientEdit',
        component: () => import('./components/agent/ClientForm.vue'),
        props: route => ({ clientId: route.params.id, isEditing: true }),
        meta: { requiresAuth: true, requiresAgentRole: true }
      },
      {
        path: 'clients/new-mobile',
        name: 'AgentClientAddNewMobile',
        component: () => import('@/components/agent/ClientForm.vue'),
        meta: { requiresAuth: true, viewMode: 'mobileForm' }
      },
      {
        path: 'clients/edit-mobile/:clientId',
        name: 'AgentClientEditMobile',
        component: () => import('@/components/agent/ClientForm.vue'),
        props: route => ({ clientId: route.params.clientId, isEditingMobile: true }),
        meta: { requiresAuth: true, viewMode: 'mobileForm' }
      },
      // OCULTO: Ruta de gestión de contratos tradicionales deshabilitada para agentes
      // { path: 'contracts', name: 'AgentContracts', component: () => import('./components/agent/ContractManagement.vue') },
      { path: 'colivings', name: 'AgentColivings', component: () => import('./components/agent/ColivingManagement.vue') },
      {
        path: 'incidents',
        name: 'AgentIncidents',
        component: () => import(/* webpackChunkName: "agent-incidents" */ './views/agent/AgentIncidentsView.vue'),
        meta: {
          requiresAuth: true,
          requiresAgentRole: true,
          title: 'Registro de Incidencias'
        }
      },
      {
        path: 'invoicing',
        name: 'AgentInvoicing',
        component: () => import(/* webpackChunkName: "agent-invoicing" */ './views/agent/InvoicingView.vue'),
        meta: {
          requiresAuth: true,
          requiresAgentRole: true,
          title: 'Facturación'
        }
      },
      {
        path: 'colivings-dashboard',
        name: 'AgentColivingsDashboard',
        component: () => import(/* webpackChunkName: "colivings-dashboard" */ './components/agent/ColivingDashTable.vue'),
        meta: {
          requiresAuth: true,
          requiresAgentRole: true,
          title: 'Panel de Control - Colivings'
        }
      },
      { path: 'profile', name: 'AgentProfile', component: () => import('./components/agent/AgentProfile.vue') },
      {
        path: 'team',
        name: 'AgentTeam',
        component: () => import(/* webpackChunkName: "agent-team" */ './components/agent/TeamManagement.vue'),
        meta: {
          requiresAuth: true,
          requiresAgentRole: true,
          title: 'Gestión de Equipo',
          restrictedForBasicAgents: true
        }
      },
      {
        path: 'calendar',
        name: 'AgentCalendar',
        component: () => import(/* webpackChunkName: "agent-calendar" */ './components/agent/AgentCalendar.vue'),
        meta: {
          requiresAuth: true,
          requiresAgentRole: true,
          title: 'Calendario del Agente',
          restrictedForBasicAgents: false
        }
      },
      {
        path: 'addColiving',
        name: 'AgentAddColiving',
        component: () => import(/* webpackChunkName: "add-coliving" */ './components/agent/AddColiving.vue'),
        meta: { requiresAuth: true, requiresAgentRole: true, title: 'Crear Nuevo Coliving' }
      },
      {
        path: 'editColiving/:id',
        name: 'AgentEditColiving',
        component: () => import(/* webpackChunkName: "edit-coliving" */ './components/agent/EditColiving.vue'),
        meta: { requiresAuth: true, requiresAgentRole: true, title: 'Editar Coliving' },
        props: true
      },
      {
        path: 'colivings/:id',
        name: 'AgentColivingDetails',
        component: () => import(/* webpackChunkName: "coliving-details" */ './components/agent/ColivingDetailsView.vue'),
        meta: { requiresAuth: true, requiresAgentRole: true, title: 'Detalles del Coliving' },
        props: true
      }
    ]
  },
  // Rutas del Owner Dashboard (Propietarios)
  {
    path: '/owner-dashboard',
    component: () => import(/* webpackChunkName: "owner" */ './components/owner/OwnerLayout.vue'),
    meta: {
      requiresAuth: true,
      requiresOwnerRole: true,
      title: 'Panel de Propietario'
    },
    children: [
      {
        path: '',
        name: 'OwnerHome',
        component: () => import(/* webpackChunkName: "owner" */ './components/owner/OwnerHome.vue'),
        meta: {
          requiresAuth: true,
          requiresOwnerRole: true,
          title: 'Dashboard de Propietario'
        }
      },
      {
        path: 'colivings',
        name: 'OwnerColivings',
        component: () => import(/* webpackChunkName: "owner-colivings" */ './components/owner/OwnerColivingsView.vue'),
        meta: {
          requiresAuth: true,
          requiresOwnerRole: true,
          title: 'Mis Colivings'
        }
      },
      {
        path: 'profile',
        name: 'OwnerProfile',
        component: () => import(/* webpackChunkName: "owner-profile" */ './components/owner/OwnerProfile.vue'),
        meta: {
          requiresAuth: true,
          requiresOwnerRole: true,
          title: 'Mi Perfil'
        }
      }
    ]
  },
  // Rutas de administración del blog
  {
    path: '/admin/blog',
    name: 'AdminBlog',
    component: () => import(/* webpackChunkName: "admin-blog" */ './components/BlogPostList.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Gestión de Blog'
    }
  },
  {
    path: '/admin/blog/new',
    name: 'AdminBlogNew',
    component: () => import(/* webpackChunkName: "admin-blog" */ './components/BlogPostEditor.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Nuevo Artículo'
    }
  },
  {
    path: '/admin/blog/edit/:id',
    name: 'AdminBlogEdit',
    component: () => import(/* webpackChunkName: "admin-blog" */ './components/BlogPostEditor.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Editar Artículo'
    }
  },
  // Rutas de compatibilidad (redirigir de admin-panel a admin)
  {
    path: '/admin-panel/blog',
    redirect: '/admin/blog'
  },
  {
    path: '/admin-panel/blog/new',
    redirect: '/admin/blog/new'
  },
  {
    path: '/admin-panel/blog/edit/:id',
    redirect: to => `/admin/blog/edit/${to.params.id}`
  },
  // Verificación de cuentas (antes de /:slug para que no las capture el blog)
  {
    path: '/verify-client',
    name: 'VerifyClient',
    component: () => import('./components/agent/VerifyClientPage.vue'),
    meta: {
      requiresAuth: false,
      title: 'Verificar Cuenta Cliente'
    }
  },
  {
    path: '/verify-guarantor',
    name: 'VerifyGuarantor',
    component: VerifyGuarantor,
    meta: {
      requiresAuth: false,
      title: 'Verificar Avalista'
    }
  },
  {
    path: '/verify-tenant-invitation',
    name: 'VerifyTenantInvitation',
    component: () => import(/* webpackChunkName: "tenant-invitation" */ './views/VerifyTenantInvitation.vue'),
    meta: {
      requiresAuth: false,
      title: 'Confirmar Invitación de Inquilino'
    }
  },
  {
    path: '/404',
    name: 'NotFoundPage',
    component: lazyLoadComponent(() => import('./components/NotFound.vue')),
    meta: {
      requiresAuth: false,
      title: 'Página no encontrada',
      noIndex: true
    }
  },
  // Posts de blog por slug (captura un solo segmento desconocido → BlogPostView → 404 si no existe)
  {
    path: '/:slug',
    name: 'BlogPostView',
    component: () => import('./components/BlogPostView.vue'),
    props: true,
    meta: {
      requiresAuth: false,
      title: 'Artículo',
      transition: 'fade-slide',
      keepAlive: false,
      forceScroll: true
    }
  },
  // Catch-all 404 (debe ser la última ruta)
  {
    path: '/:pathMatch(.*)*',
    component: lazyLoadComponent(() => import('./components/NotFound.vue')),
    name: 'NotFound',
    meta: {
      requiresAuth: false,
      title: 'Página no encontrada',
      noIndex: true
    }
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Si hay una posición guardada (botón atrás/adelante), usarla
    if (savedPosition) {
      return savedPosition;
    }

    // Si la ruta tiene propiedad forceScroll, ir al inicio siempre
    if (to.meta.forceScroll) {
      return {
        top: 0,
        behavior: 'auto' // Usar 'auto' en lugar de 'smooth' para mayor rapidez
      };
    }

    // Si hay un hash en la URL (ancla), scroll a esa posición
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
        top: 80 // Offset para header fijo
      };
    }

    // De lo contrario, ir al inicio de la página
    return { top: 0, behavior: 'smooth' };
  }
});

// Prefetching de rutas populares más inteligente
const prefetchImportant = () => {
  if (typeof window === 'undefined' || (navigator.connection && navigator.connection.saveData)) {
    // No prefetch en SSR o si el usuario tiene modo de ahorro de datos activado
    return;
  }

  // Priorizar componentes basados en navegación típica
  const prefetchQueue = [
    // Componentes críticos
    () => import('./components/ViewContractPublic.vue'),
    () => import('./components/UserPanel.vue'),

    // Componentes secundarios
    () => import('./components/Profile.vue'),
    () => import('./components/BlogPostView.vue'),
    () => import('./components/AdvancedSearch.vue'),
    () => import('./components/ViewColivingPublic.vue'),

    // Componentes raramente usados
    () => import('./components/NotFound.vue'),
    () => import('./components/ForgotPassword.vue')
  ];

  // Usar requestIdleCallback para cargar cuando el navegador esté inactivo
  if ('requestIdleCallback' in window) {
    prefetchQueue.forEach((importFn, index) => {
      window.requestIdleCallback(() => {
        // Esperar más tiempo para componentes de menor prioridad
        setTimeout(() => {
          importFn().catch(() => { /* Ignorar errores de precarga */ });
        }, index * 1000); // 1s de retraso adicional por cada componente
      }, { timeout: 10000 });
    });
  } else {
    // Fallback para navegadores sin requestIdleCallback
    setTimeout(() => {
      prefetchQueue.forEach((importFn, index) => {
        setTimeout(() => {
          importFn().catch(() => { /* Ignorar errores de precarga */ });
        }, index * 2000); // 2s de retraso adicional por cada componente
      });
    }, 3000);
  }
};

// Iniciar prefetch después de cargar la página principal
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    setTimeout(prefetchImportant, 2000);
  });
}

// Espera al estado real de Firebase Auth (una sola resolución por llamada).
// No pre-resolver con caché: Promise solo acepta un resolve y el uid debe ser real.
function waitForAuthUser() {
  return getFirebaseAuthModule().then(({ auth, onAuthStateChanged }) => new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      localStorage.setItem('userAuthState', JSON.stringify({
        isAuthenticated: !!user,
        uid: user?.uid ?? null,
        email: user?.email ?? null,
        displayName: user?.displayName ?? null,
        photoURL: user?.photoURL ?? null,
        timestamp: new Date().toISOString(),
      }));
      resolve(user);
    });
  }));
}

/** Cierra sesión si accountStatus es suspended/deleted. Devuelve false si la cuenta está bloqueada. */
async function enforceActiveAccount(user) {
  if (!user?.uid) return true;

  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    const { auth, signOut } = await getFirebaseAuthModule();

    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) return true;

    const status = userDoc.data().accountStatus;
    if (!['suspended', 'deleted'].includes(status)) return true;

    await signOut(auth);
    localStorage.removeItem('userAuthState');
    localStorage.removeItem('authUser');
    return false;
  } catch (error) {
    console.error('[Router] Error comprobando accountStatus:', error);
    return true;
  }
}

/** Comprueba que el usuario Firebase tenga cuenta activa en operatorAccounts. */
async function verifyOperatorAccess(user) {
  if (!user?.uid) return false;
  try {
    const token = await user.getIdToken(false);
    const res = await fetch('/api/operator-leads/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.ok;
  } catch {
    return false;
  }
}

// ── Tracking global de visitas web ──────────────────────────────────────────
// Cuenta cada navegación como 1 visita (enter). Más robusto que depender del
// onMounted de componentes individuales: funciona en lazy-load, errores JS, etc.
router.afterEach((to) => {
  sendRouterPageView(to)
});

// Timeouts de navegación blog/coliving — un solo afterEach los limpia (no registrar handlers en bucle)
let blogNavigationFixTimeout = null;
let colivingNavigationFixTimeout = null;

// Títulos de páginas dinámicos
router.afterEach((to) => {
  if (blogNavigationFixTimeout !== null) {
    clearTimeout(blogNavigationFixTimeout);
    blogNavigationFixTimeout = null;
    sessionStorage.removeItem('blog_navigation_fix');
  }
  if (colivingNavigationFixTimeout !== null) {
    clearTimeout(colivingNavigationFixTimeout);
    colivingNavigationFixTimeout = null;
    sessionStorage.removeItem('coliving_navigation_fix');
  }

  // Establecer el título del documento basado en la ruta
  const baseTitle = 'Coolliving';
  let pageTitle = to.meta.title;

  if (to.path === '/operator/login') {
    pageTitle = i18n.global.t('operatorPortal.meta.loginTitle');
  } else if (to.path === '/operator/set-password') {
    pageTitle = i18n.global.t('operatorPortal.meta.setPasswordTitle');
  } else if (to.path === '/leads-marketplace') {
    pageTitle = i18n.global.t('operadoresLeadsLanding.meta.title');
  } else if (to.path.startsWith('/operator/leads')) {
    pageTitle = i18n.global.t('operatorPortal.meta.leadsTitle');
  } else if (to.path.startsWith('/operator/how-it-works')) {
    pageTitle = i18n.global.t('operatorPortal.meta.howItWorksTitle');
  }

  if (pageTitle) {
    document.title = `${pageTitle} | ${baseTitle}`;
  } else {
    document.title = baseTitle;
  }
  
  // Gestionar meta robots para rutas privadas (noIndex)
  let robotsMeta = document.querySelector('meta[name="robots"]');
  if (to.meta.noIndex || to.meta.requiresAuth) {
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', 'noindex, nofollow');
  } else if (robotsMeta && !robotsMeta.dataset.managed) {
    // Solo resetear si no está gestionado por otro componente (ColivingSearchSEO, SimpleSeoHead, etc.)
    robotsMeta.setAttribute('content', 'index, follow');
  }
});

// Optimizar navegación
router.beforeEach(async (to, from, next) => {
  // Performance mark inicio de la navegación
  if (typeof window !== 'undefined' && 'performance' in window) {
    performance.mark('route-change-start');
  }

  // Tráfico pagado → página de ciudad (UTM/fbclid/gclid). Orgánico sigue en root.
  const paidCityRedirect = resolvePaidTrafficRedirect(to);
  if (paidCityRedirect) {
    return next({
      path: paidCityRedirect.path,
      query: paidCityRedirect.query,
      hash: to.hash,
      replace: true,
    });
  }

  // Logging adicional para debugging


  // NUEVA SOLUCIÓN: Manejo especial para navegación desde /blog a otras rutas
  if (from.path.startsWith('/blog') && !to.path.startsWith('/blog')) {
    // console.log(`[Router] Aplicando solución especial para navegación desde /blog a ${to.path}`);

    // Opción 1: Forzar que el componente no use keepAlive
    if (to.meta && to.meta.keepAlive) {
      //  console.log('[Router] Desactivando keepAlive temporalmente');
      to.meta._originalKeepAlive = to.meta.keepAlive;
      to.meta.keepAlive = false;

      // Restaurar después de la navegación
      setTimeout(() => {
        if (to.meta && to.meta._originalKeepAlive !== undefined) {
          to.meta.keepAlive = to.meta._originalKeepAlive;
          delete to.meta._originalKeepAlive;
          // console.log('[Router] Restaurado keepAlive a su valor original');
        }
      }, 1000);
    }

    // Opción 2: Solución temporal mediante recarga
    const shouldReload = sessionStorage.getItem('blog_navigation_fix');
    if (shouldReload === 'pending') {
      sessionStorage.removeItem('blog_navigation_fix');
      sessionStorage.setItem('router_reload_destination', to.fullPath);
      //console.log('[Router] Aplicando solución de recarga para navegación desde /blog');
      window.location.href = to.fullPath;
      return next(false); // Detener la navegación normal
    }

    // Marcar para posible recarga en caso de error
    sessionStorage.setItem('blog_navigation_fix', 'pending');

    // Configurar un timeout para detectar si la navegación se completa correctamente
    if (blogNavigationFixTimeout !== null) {
      clearTimeout(blogNavigationFixTimeout);
    }
    blogNavigationFixTimeout = setTimeout(() => {
      if (sessionStorage.getItem('blog_navigation_fix') === 'pending') {
        window.location.href = to.fullPath;
      }
    }, 1000);
  }

  // NUEVA SOLUCIÓN: Manejo especial para navegación desde /encuentratucoliving a otras rutas
  if (from.path.startsWith('/encuentratucoliving') && !to.path.startsWith('/encuentratucoliving')) {
    // Opción 1: Forzar que el componente no use keepAlive
    if (to.meta && to.meta.keepAlive) {
      to.meta._originalKeepAlive = to.meta.keepAlive;
      to.meta.keepAlive = false;

      // Restaurar después de la navegación
      setTimeout(() => {
        if (to.meta && to.meta._originalKeepAlive !== undefined) {
          to.meta.keepAlive = to.meta._originalKeepAlive;
          delete to.meta._originalKeepAlive;
        }
      }, 1000);
    }

    // Opción 2: Solución temporal mediante recarga
    const shouldReload = sessionStorage.getItem('coliving_navigation_fix');
    if (shouldReload === 'pending') {
      sessionStorage.removeItem('coliving_navigation_fix');
      sessionStorage.setItem('router_reload_destination', to.fullPath);
      window.location.href = to.fullPath;
      return next(false); // Detener la navegación normal
    }

    // Marcar para posible recarga en caso de error
    sessionStorage.setItem('coliving_navigation_fix', 'pending');

    // Configurar un timeout para detectar si la navegación se completa correctamente
    if (colivingNavigationFixTimeout !== null) {
      clearTimeout(colivingNavigationFixTimeout);
    }
    colivingNavigationFixTimeout = setTimeout(() => {
      if (sessionStorage.getItem('coliving_navigation_fix') === 'pending') {
        window.location.href = to.fullPath;
      }
    }, 1000);
  }

  // PRIORIDAD MÁXIMA: Procesamiento especial para rutas críticas
  if (to.path.startsWith('/contracts/') && /^\/contracts\/[a-zA-Z0-9-_]+$/.test(to.path)) {
    return next();
  }

  // Portal operador: auth propia (no usa requiresAuth ni users collection)
  if (to.path.startsWith('/operator')) {
    const operatorUser = await waitForAuthUser();
    const operatorAuthenticated = !!operatorUser;
    const requiresOperatorRoute = to.matched.some((r) => r.meta.requiresOperator);

    if (requiresOperatorRoute) {
      if (!operatorAuthenticated) {
        return next('/operator/login');
      }
      const accountActive = await enforceActiveAccount(operatorUser);
      if (!accountActive) {
        return next('/operator/login');
      }
      const isOperator = await verifyOperatorAccess(operatorUser);
      if (!isOperator) {
        return next('/operator/login');
      }
      return next();
    }

    if (to.path === '/operator/login' && operatorAuthenticated) {
      const isOperator = await verifyOperatorAccess(operatorUser);
      if (isOperator) {
        return next('/operator/leads');
      }
    }

    return next();
  }

  // Optimizar para rutas que no requieren verificación
  if (!to.matched.some(record =>
    record.meta.requiresAuth ||
    record.meta.requiresSuperuser ||
    record.meta.requiresAdmin ||
    record.meta.requiresAgentRole ||
    record.meta.requiresOperator
  )) {
    return next();
  }

  const authenticatedUser = await waitForAuthUser();
  const isAuthenticated = !!authenticatedUser;

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresCompleteProfile = to.matched.some(record => record.meta.requiresCompleteProfile);
  const requiresSuperuser = to.matched.some(record => record.meta.requiresSuperuser);
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);
  const requiresApplicantRole = to.matched.some(record => record.meta.requiresApplicantRole);
  const requiresAgentRole = to.matched.some(record => record.meta.requiresAgentRole);
  const requiresOwnerRole = to.matched.some(record => record.meta.requiresOwnerRole);
  const allowInvited = to.matched.some(record => record.meta.allowInvited);
  const allowAuthenticated = to.matched.some(record => record.meta.allowAuthenticated);

  // Check for development mode auth
  const isDevelopment = process.env.NODE_ENV === 'development' || process.env.VUE_APP_ENV === 'development';
  const devAuthUser = isDevelopment ? JSON.parse(sessionStorage.getItem('dev_auth_user') || 'null') : null;

  // Si la ruta permite usuarios autenticados (como ViewContractPublic), permitir acceso sin importar el estado de autenticación
  if (allowAuthenticated) {
    return next();
  }

  if (isAuthenticated && !(isDevelopment && devAuthUser)) {
    const accountActive = await enforceActiveAccount(authenticatedUser);
    if (!accountActive) {
      return next('/login');
    }
  }

  if (requiresAuth && !isAuthenticated) {
    // If we're in development mode and have a dev auth user, allow access
    if (isDevelopment && devAuthUser) {
      next();
    } else if (allowInvited && to.name === 'ViewContractPublic') {
      // For invited users, allow access to ViewContractPublic but store the path for post-login redirect
      next({ path: '/login', query: { redirect: to.fullPath } });
    } else {
      next('/login');
    }
  } else if (requiresSuperuser && isAuthenticated) {
    // Verificar si el usuario es admin
    try {
      // Obtener el tipo de usuario del localStorage si está disponible
      const cachedUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      let userType = cachedUser.userType;

      // Si no está en caché, consultarlo de Firestore
      if (!userType) {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('./firebase');

        const userDoc = await getDoc(doc(db, 'users', authenticatedUser.uid));
        if (userDoc.exists()) {
          userType = userDoc.data().userType;
        }
      }

      // Verificar si el usuario es de tipo 'admin'
      if (userType === 'admin') {
        next();
      } else {
        // Redirigir a la página principal si no es admin
        next('/');
      }
    } catch (error) {
      console.error('Error verificando permisos de admin:', error);
      next('/');
    }
  } else if (requiresAdmin && isAuthenticated) {
    // Verificar si el usuario es admin usando el userType en Firestore
    try {
      // Obtener el tipo de usuario del localStorage si está disponible
      const cachedUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      let userType = cachedUser.userType;

      // Si no está en caché, consultarlo de Firestore
      if (!userType) {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('./firebase');

        const userDoc = await getDoc(doc(db, 'users', authenticatedUser.uid));
        if (userDoc.exists()) {
          userType = userDoc.data().userType;
        }
      }

      // Verificar si el usuario es de tipo 'admin'
      if (userType === 'admin') {
        next();
      } else {
        // Redirigir al dashboard correspondiente a su tipo
        next(getRedirectByUserType(userType) || '/');
      }
    } catch (error) {
      console.error('Error verificando permisos de admin:', error);
      next('/');
    }
  } else if (requiresAgentRole && isAuthenticated) {
    // Verificar si el usuario es de tipo 'agent' o admin
    // Los usuarios admin pueden acceder a todas las rutas de agente
    try {
      // Obtener el tipo de usuario del localStorage si está disponible
      const cachedUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      let userType = cachedUser.userType;

      // Si no está en caché, consultarlo de Firestore
      if (!userType) {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('./firebase');

        const userDoc = await getDoc(doc(db, 'users', authenticatedUser.uid));
        if (userDoc.exists()) {
          userType = userDoc.data().userType;
        }
      }

      // Los usuarios admin pueden acceder a todas las rutas de agente
      if (userType === 'admin') {
        next();
      } else if (userType === 'agent') {
        // NUEVO: Verificar restricciones para Basic Agents
        const isRestrictedForBasicAgents = to.matched.some(record => record.meta.restrictedForBasicAgents);

        if (isRestrictedForBasicAgents) {
          try {
            const agentInfo = await getCurrentAgentInfo();

            if (agentInfo?.agentRole === AGENT_ROLES.BASIC) {
              // Redirigir Basic Agents con mensaje de upgrade
              next({
                path: '/agent-dashboard',
                query: {
                  upgrade: 'required',
                  feature: to.meta.title || 'esta funcionalidad',
                  restricted: 'basic'
                }
              });
              return;
            }
          } catch (error) {
            console.error('Error verificando rol de agente:', error);
            // En caso de error, permitir acceso para no bloquear la aplicación
          }
        }

        next();
      } else {
        // Redirigir al dashboard correspondiente a su tipo
        next(getRedirectByUserType(userType));
      }
    } catch (error) {
      console.error('Error verificando tipo de usuario agent:', error);
      next('/');
    }
  } else if (requiresOwnerRole && isAuthenticated) {
    // Verificar si el usuario es de tipo 'managedOwner' SOLAMENTE
    try {
      // Obtener el tipo de usuario del localStorage si está disponible
      const cachedUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      let userType = cachedUser.userType;

      // Si no está en caché, consultarlo de Firestore
      if (!userType) {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('./firebase');

        const userDoc = await getDoc(doc(db, 'users', authenticatedUser.uid));
        if (userDoc.exists()) {
          userType = userDoc.data().userType;
        }
      }

      // Verificar si el usuario es de tipo 'managedOwner' SOLAMENTE
      if (userType === 'managedOwner') {
        next();
      } else {
        // Redirigir al dashboard correspondiente a su tipo
        next(getRedirectByUserType(userType));
      }
    } catch (error) {
      console.error('Error verificando tipo de usuario managedOwner:', error);
      next('/');
    }
  } else if (requiresApplicantRole && isAuthenticated) {
    // Verificar si el usuario es de tipo 'applicant'
    try {
      // Obtener el tipo de usuario del localStorage si está disponible
      const cachedUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      let userType = cachedUser.userType;

      // Si no está en caché, consultarlo de Firestore
      if (!userType) {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('./firebase');

        const userDoc = await getDoc(doc(db, 'users', authenticatedUser.uid));
        if (userDoc.exists()) {
          userType = userDoc.data().userType;
        }
      }

      // Verificar si el usuario es de tipo 'applicant'
      if (userType === 'applicant') {
        next();
      } else {
        // Redirigir al dashboard correspondiente a su tipo
        next(getRedirectByUserType(userType));
      }
    } catch (error) {
      console.error('Error verificando tipo de usuario applicant:', error);
      next('/');
    }
  } else if (requiresCompleteProfile && isAuthenticated) {
    // Check if the user has completed their profile
    try {
      // Primero verificar si tenemos el estado en caché
      const cachedProfileData = localStorage.getItem('userProfileState');
      let hasCompletedProfile = false;

      if (cachedProfileData) {
        try {
          const profileData = JSON.parse(cachedProfileData);
          hasCompletedProfile = profileData.hasCompletedProfile;

          // Si la caché indica que el perfil está completo, permitir el acceso inmediato
          if (hasCompletedProfile) {
            // Verificar si la ruta requiere un tipo de usuario específico
            if (to.meta.userType) {
              // Obtener el tipo de usuario del localStorage si está disponible
              const cachedUser = JSON.parse(localStorage.getItem('authUser') || '{}');
              const userType = cachedUser.userType;

              // Verificar si el usuario tiene acceso a esta ruta según su tipo
              if (!hasAccess(userType, to.meta.userType)) {
                // Redirigir al dashboard correspondiente a su tipo
                return next(getRedirectByUserType(userType));
              }
            }
            return next();
          }
        } catch (e) {
          console.error("Error al analizar la caché del perfil:", e);
        }
      }

      // Si no hay caché o la caché indica perfil incompleto, verificar con Firestore
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('./firebase');

      const userDoc = await getDoc(doc(db, 'users', authenticatedUser.uid)); // Use authenticatedUser.uid

      if (userDoc.exists()) {
        const userData = userDoc.data();

        // Los agentes no necesitan completar el perfil básico (lo harán en AgentProfile)
        if (userData.userType === 'agent') {
          hasCompletedProfile = true;
        } else {
          hasCompletedProfile = !!(
            userData.dni &&
            userData.dniFrontPictureUrl &&
            (userData.userType !== 'owner' || userData.bankNumber)
          );
        }

        // Actualizar la caché
        localStorage.setItem('userProfileState', JSON.stringify({
          hasCompletedProfile: hasCompletedProfile,
          lastChecked: new Date().toISOString()
        }));

        if (!hasCompletedProfile) {
          // Redirigir según el tipo de usuario:
          // - applicant: va a /user-dashboard (panel consolidado)
          // - owner: va a /complete-profile (necesita cuenta bancaria)
          const redirectPath = userData.userType === 'applicant' ? '/user-dashboard' : '/complete-profile';
          next(redirectPath);
        } else {
          // Verificar si la ruta requiere un tipo de usuario específico
          if (to.meta.userType && !hasAccess(userData.userType, to.meta.userType)) {
            // Redirigir al dashboard correspondiente a su tipo
            return next(getRedirectByUserType(userData.userType));
          }
          next();
        }
      } else {
        // Actualizar la caché indicando perfil incompleto
        localStorage.setItem('userProfileState', JSON.stringify({
          hasCompletedProfile: false,
          lastChecked: new Date().toISOString()
        }));

        // Por defecto, ir a user-dashboard si no tenemos datos del usuario
        next('/user-dashboard');
      }
    } catch (error) {
      console.error('Error verificando perfil:', error);
      next('/user-dashboard');
    }
  } else {
    // Verificar si la ruta requiere un tipo de usuario específico
    if (to.meta.userType && isAuthenticated) {
      try {
        // Obtener el tipo de usuario del localStorage si está disponible
        const cachedUser = JSON.parse(localStorage.getItem('authUser') || '{}');
        let userType = cachedUser.userType;

        // Si no está en caché, consultarlo de Firestore
        if (!userType) {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('./firebase');

          const userDoc = await getDoc(doc(db, 'users', authenticatedUser.uid));
          if (userDoc.exists()) {
            userType = userDoc.data().userType;
          }
        }

        // Verificar si el usuario tiene acceso a esta ruta según su tipo
        if (!hasAccess(userType, to.meta.userType)) {
          // Redirigir al dashboard correspondiente a su tipo
          return next(getRedirectByUserType(userType));
        }
      } catch (error) {
        console.error('Error verificando tipo de usuario:', error);
      }
    }
    next();
  }
});

// Performance tracking y diagnóstico de navegación
router.afterEach((to, from) => {
  // Diagnóstico de navegación

  // Mover todo el código de diagnóstico y performance dentro del if
  if (process.env.NODE_ENV === 'development') {
    // Detección especial para navegación desde /blog


    // Verificar el estado de la caché y almacenar información
    try {
      const navigationLog = JSON.parse(sessionStorage.getItem('navigation_log') || '[]');
      navigationLog.push({
        from: from.path,
        to: to.path,
        timestamp: new Date().toISOString(),
        success: true
      });
      // Mantener solo las últimas 10 navegaciones
      if (navigationLog.length > 10) {
        navigationLog.shift();
      }
      sessionStorage.setItem('navigation_log', JSON.stringify(navigationLog));
    } catch (e) {
      console.error('[Router] Error al registrar navegación:', e);
    }

    // Performance tracking original
    if (typeof window !== 'undefined' && 'performance' in window) {
      performance.mark('route-change-end');
      performance.measure(
        `Tiempo de navegación a ${to.path}`,
        'route-change-start',
        'route-change-end'
      );

      // Registrar la medición en consola en modo desarrollo (ya estaba dentro de un if, pero lo mantenemos aquí por claridad)
      const navigationTimings = performance.getEntriesByType('measure');

    }
  } // Fin del if (process.env.NODE_ENV === 'development')
});

// Capturar errores de navegación
router.onError((error) => {
  console.error('[Router] Error durante la navegación:', error);

  // Registrar el error en el registro de navegación solo en desarrollo
  if (process.env.NODE_ENV === 'development') {
    try {
      const navigationLog = JSON.parse(sessionStorage.getItem('navigation_log') || '[]');
      navigationLog.push({
        error: error.message,
        stack: error.stack,
        route: router.currentRoute.value.path,
        timestamp: new Date().toISOString(),
        success: false
      });
      sessionStorage.setItem('navigation_log', JSON.stringify(navigationLog));
    } catch (e) {
      // Ignore logging errors
    }
  }
});

// Funciones helper para gestionar tipos de usuario y redirecciones
const hasAccess = (userType, requiredType) => {
  if (!requiredType) return true;

  // El superusuario (admin) tiene acceso a todo
  if (userType === 'admin') return true;

  if (requiredType === 'admin') return userType === 'admin';
  if (requiredType === 'agent') return userType === 'agent';
  return true;
};

const getRedirectByUserType = (userType) => {
  switch (userType) {
    case 'admin':
      return '/admin';
    case 'agent':
      return '/agent-dashboard';
    case 'applicant':
      return '/contracts';
    case 'managedOwner':
      return '/owner-dashboard';
    case 'owner':
    default:
      return '/contracts';
  }
};

export default router